import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { 
  MapPin, Calendar, DollarSign, Clock, Phone, MessageCircle, 
  Navigation, Check, X, Menu, LogOut, Power, ChevronRight,
  AlertCircle, TrendingUp, Package, User, Bell, Camera, Key, Bike
} from 'lucide-react';
import { auth, db, app } from './firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs, getDoc, orderBy, setDoc, serverTimestamp, addDoc, arrayUnion } from 'firebase/firestore';


const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        className="w-full text-left p-3 font-semibold text-gray-800 flex items-center justify-between"
        onClick={() => setOpen(!open)}
      >
        {question}
        <span>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <p className="text-sm text-gray-600 px-3 pb-3">{answer}</p>
      )}
    </div>
  );
};

// Fix icone Leaflet con webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const LeafletMap = memo(({ riderLocation, activeOrder }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const riderMarkerRef = useRef(null);
  const customerMarkerRef = useRef(null);

  // Icona rider verde
  const riderIcon = L.divIcon({
    html: `<div style="
      width:36px;height:36px;border-radius:50%;
      background:#16a34a;border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
      display:flex;align-items:center;justify-content:center;
    ">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    className: ''
  });

  // Icona cliente rosso
  const customerIcon = L.divIcon({
    html: `<div style="
      width:32px;height:32px;border-radius:50%;
      background:#ef4444;border:3px solid #fff;
      box-shadow:0 2px 8px rgba(0,0,0,0.3);
      display:flex;align-items:center;justify-content:center;
    ">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    className: ''
  });

  // Inizializza mappa una sola volta
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = L.map(mapRef.current, {
      center: [riderLocation.lat, riderLocation.lng],
      zoom: 15,
      zoomControl: true,
      attributionControl: false
    });

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
      maxZoom: 19
    }).addTo(mapInstanceRef.current);

    riderMarkerRef.current = L.marker(
      [riderLocation.lat, riderLocation.lng],
      { icon: riderIcon }
    ).addTo(mapInstanceRef.current);

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Aggiorna posizione rider senza reinizializzare
  useEffect(() => {
    if (!mapInstanceRef.current || !riderMarkerRef.current) return;
    const { lat, lng } = riderLocation;
    riderMarkerRef.current.setLatLng([lat, lng]);
    mapInstanceRef.current.setView([lat, lng], mapInstanceRef.current.getZoom(), { animate: true });
  }, [riderLocation]);

  // Aggiorna marker cliente quando c'è un ordine attivo
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    if (activeOrder?.deliveryLat && activeOrder?.deliveryLng) {
      if (customerMarkerRef.current) {
        customerMarkerRef.current.setLatLng([activeOrder.deliveryLat, activeOrder.deliveryLng]);
      } else {
        customerMarkerRef.current = L.marker(
          [activeOrder.deliveryLat, activeOrder.deliveryLng],
          { icon: customerIcon }
        ).addTo(mapInstanceRef.current);
      }
      // Zoom per includere entrambi i marker
      const bounds = L.latLngBounds(
        [riderLocation.lat, riderLocation.lng],
        [activeOrder.deliveryLat, activeOrder.deliveryLng]
      );
      mapInstanceRef.current.fitBounds(bounds, { padding: [40, 40], animate: true });
    } else {
      if (customerMarkerRef.current) {
        customerMarkerRef.current.remove();
        customerMarkerRef.current = null;
      }
    }
  }, [activeOrder]);

  return (
    <div
      ref={mapRef}
      style={{ width: '100%', height: '400px', borderRadius: '12px', overflow: 'hidden' }}
    />
  );
});

// SUPPORT VIEW
const SupportView = ({ currentRider, db }) => {
  const [realtimeMessages, setRealtimeMessages] = React.useState([]);
  const [supportInput, setSupportInput] = React.useState('');
  const [showChat, setShowChat] = React.useState(false);
  const [ticketId, setTicketId] = React.useState(null);
  const messagesEndRef = React.useRef(null);

  React.useEffect(() => {
    if (!currentRider?.id || !showChat) return;
    const loadOrCreateTicket = async () => {
      // Query senza disuguaglianza — filtra in memoria per evitare indice composto mancante
      const q = query(
        collection(db, 'support-tickets'),
        where('riderId', '==', String(currentRider.id))
      );
      const snapshot = await getDocs(q);
      const ticketAperto = snapshot.docs.find(d => d.data().status !== 'closed');
      if (ticketAperto) {
        setTicketId(ticketAperto.id);
      } else {
        const docRef = await addDoc(collection(db, 'support-tickets'), {
          riderId: String(currentRider.id),
          riderUid: currentRider.uid || currentRider.id,
          riderName: currentRider.name,
          riderEmail: currentRider.email || '',
          status: 'open',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        setTicketId(docRef.id);
        await addDoc(collection(db, 'support-tickets', docRef.id, 'messages'), {
          text: 'Ciao! Come possiamo aiutarti? Un operatore ti risponderà a breve. 😊',
          sender: 'admin',
          senderName: 'Supporto FarmaciaSubito',
          createdAt: serverTimestamp()
        });
      }
    };
    loadOrCreateTicket();
  }, [currentRider?.id, showChat]);

  React.useEffect(() => {
    if (!ticketId) return;
    // Nessun orderBy — evita indici composti e problemi con serverTimestamp() non ancora risolto
    const unsubscribe = onSnapshot(collection(db, 'support-tickets', ticketId, 'messages'), (snapshot) => {
      const msgs = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const tA = a.createdAt?.seconds ?? (a.createdAt ? new Date(a.createdAt).getTime() / 1000 : 0);
          const tB = b.createdAt?.seconds ?? (b.createdAt ? new Date(b.createdAt).getTime() / 1000 : 0);
          return tA - tB;
        });
      setRealtimeMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return () => unsubscribe();
  }, [ticketId]);

  const sendSupportMessage = async () => {
    const msg = supportInput.trim();
    if (!msg || !ticketId) return;
    setSupportInput('');
    try {
      await addDoc(collection(db, 'support-tickets', ticketId, 'messages'), {
        text: msg,
        sender: 'rider',
        senderName: currentRider?.name || 'Rider',
        createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, 'support-tickets', ticketId), {
        updatedAt: serverTimestamp(),
        lastMessage: msg,
        status: 'open'
      });
    } catch (error) {
      console.error('Errore invio messaggio:', error);
    }
  };

  if (showChat) {
    return (
      <div className="flex flex-col pb-24" style={{ height: '100vh' }}>
        <div className="bg-white border-b border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowChat(false)} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="rotate-180" size={20} />
            </button>
            <div>
              <h2 className="font-bold text-lg">Chat Supporto</h2>
              <p className="text-xs text-orange-600">🟠 Supporto FarmaciaSubito</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {realtimeMessages.length === 0 && (
            <div className="text-center text-gray-400 py-8">
              <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Caricamento chat...</p>
            </div>
          )}
          {realtimeMessages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender === 'rider' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${
                msg.sender === 'rider'
                  ? 'bg-orange-600 text-white'
                  : 'bg-white text-gray-800 border border-gray-200'
              } p-3 rounded-2xl shadow-sm`}>
                {msg.sender === 'admin' && (
                  <p className="text-xs font-bold text-orange-600 mb-1">Supporto FarmaciaSubito</p>
                )}
                <p className="text-sm">{msg.text}</p>
                <p className={`text-xs mt-1 ${msg.sender === 'rider' ? 'text-orange-100' : 'text-gray-400'}`}>
                  {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '...'}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={supportInput}
              onChange={(e) => setSupportInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendSupportMessage()}
              placeholder="Scrivi al supporto..."
              className="flex-1 px-4 py-3 rounded-xl bg-gray-100 border-0 focus:ring-2 focus:ring-orange-500 outline-none text-sm"
            />
            <button
              onClick={sendSupportMessage}
              className="bg-orange-600 text-white px-5 py-3 rounded-xl font-semibold hover:bg-orange-700 transition"
            >
              Invia
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="bg-white p-4 rounded-xl shadow-md">
        <h2 className="text-xl font-bold mb-4">Supporto Rider</h2>
        <div className="space-y-3">
          <button
            onClick={() => setShowChat(true)}
            className="w-full bg-orange-50 border border-orange-200 p-4 rounded-lg hover:bg-orange-100 transition text-left"
          >
            <div className="flex items-center gap-3">
              <MessageCircle className="text-orange-600" size={24} />
              <div>
                <p className="font-semibold text-gray-800">Chat con operatore</p>
                <p className="text-sm text-gray-500">Risposta in 2-5 minuti</p>
              </div>
            </div>
          </button>
        </div>
      </div>
      <div className="bg-white p-4 rounded-xl shadow-md">
        <h3 className="font-bold mb-3">Domande Frequenti</h3>
        <div className="space-y-3">
          {[
            { q: 'Quando ricevo i pagamenti?', a: 'I guadagni vengono accreditati ogni inizio mese, entro il giorno 12.' },
            { q: 'Come annullo un turno?', a: 'Puoi annullare turni confermati fino a 24 ore prima. Dopo, contatta il supporto.' },
            { q: 'Cosa fare se ho un problema durante la consegna?', a: 'Usa la chat con il cliente o contatta immediatamente il supporto.' },
            { q: 'Come funzionano i guadagni?', a: 'Guadagni una tariffa base per consegna più eventuali mance dei clienti.' },
            { q: "Problemi con l'app?", a: "Prova a riavviare l'app. Se il problema persiste, contattaci via chat." }
          ].map((faq, idx) => (
            <FaqItem key={idx} question={faq.q} answer={faq.a} />
          ))}
        </div>
      </div>
      <div className="bg-orange-50 border border-orange-200 p-4 rounded-xl">
        <p className="text-sm text-orange-800">
          <strong>💡 Suggerimento:</strong> Per problemi urgenti durante una consegna, usa la chat qui sopra per assistenza immediata.
        </p>
      </div>
    </div>
  );
};

// Campo importo pagato in farmacia (ordini ricetta). DEVE stare FUORI dal
// componente FarmaciaSubitoRider: se definito dentro, verrebbe ricreato a ogni
// render (es. aggiornamento GPS ogni 7s) rimontando l'input e facendo perdere
// il focus alla tastiera mentre il rider scrive.
const ImportoRicettaInput = React.memo(({ valoreIniziale, onCommit, scontrinoUrl }) => {
  // Stato interno completo: il campo vive di vita propria durante la digitazione
  // e notifica il parent SOLO quando il rider smette di scrivere (onBlur) o preme
  // invio. Così i re-render del parent non lo toccano e la tastiera non si chiude.
  const [testo, setTesto] = React.useState(valoreIniziale || '');

  const handleChange = (e) => {
    const v = e.target.value.replace(/[^0-9.,]/g, '').replace(',', '.');
    setTesto(v);
  };

  const commit = () => { onCommit(testo); };

  const importoValido = testo && !isNaN(parseFloat(testo));

  return (
    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 space-y-2">
      <p className="text-xs font-black text-orange-800">🧾 Ordine con Ricetta — Quanto hai pagato in farmacia?</p>
      <p className="text-xs text-orange-700">
        Inserisci l'importo che hai pagato in farmacia per il farmaco. Serve come riepilogo per l'amministrazione
        (l'eventuale differenza la salda il cliente alla consegna). Usa punto o virgola per i centesimi (es. 12.50 o 12,50).
      </p>
      <div className="flex items-center gap-2 bg-white rounded-lg border border-orange-300 px-3 focus-within:border-orange-500">
        <span className="text-orange-700 font-black">€</span>
        <input
          type="text"
          inputMode="decimal"
          placeholder="es. 12.50"
          value={testo}
          onChange={handleChange}
          onBlur={commit}
          className="flex-1 py-2 text-sm outline-none bg-transparent"
        />
      </div>
      {importoValido && (
        <p className="text-xs text-orange-600 font-semibold">
          Importo: €{parseFloat(testo).toFixed(2)}
        </p>
      )}
      {scontrinoUrl && (
        <button onClick={() => window.open(scontrinoUrl, '_blank')}
          className="text-xs text-blue-600 font-bold">👁️ Vedi scontrino caricato</button>
      )}
    </div>
  );
}, () => true); // MAI re-render da parte del parent: il campo si autogestisce

const FarmaciaSubitoRider = () => {
  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentRider, setCurrentRider] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  // App State
  const [currentView, setCurrentView] = useState('map');
  const [isOnline, setIsOnline] = useState(false);
  const [currentShift, setCurrentShift] = useState(null);
  const [totalEarnings, setTotalEarnings] = useState(247.80);
  const [todayEarnings, setTodayEarnings] = useState(0);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [totalEarningsFromDB, setTotalEarningsFromDB] = useState(0);
  const [todayEarningsFromDB, setTodayEarningsFromDB] = useState(0);
  const [earningsFilter, setEarningsFilter] = useState('today'); // today, week, month, all
  const [showDailyDisclaimer, setShowDailyDisclaimer] = useState(false);
  
  // Order State
  const [incomingOrder, setIncomingOrder] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [riderLocation, setRiderLocation] = useState({ lat: 41.1297, lng: 14.7824 });
  const riderLocationRef = useRef({ lat: 41.1297, lng: 14.7824 });
  const [orderTimer, setOrderTimer] = useState(25);
  const [showNotification, setShowNotification] = useState(false);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [showProdottoNonTrovato, setShowProdottoNonTrovato] = useState(false);
  const [ricettaFullscreen, setRicettaFullscreen] = useState(null);
  const [showCodiceFiscalePopup, setShowCodiceFiscalePopup] = useState(false);
  const [codiceFiscaleStep, setCodiceFiscaleStep] = useState(1);
  const [prodottiRitirati, setProdottiRitirati] = useState(false);
  const [scontrinoUrl, setScontrinoUrl] = useState(null);
  const [importoPagato, setImportoPagato] = useState('');
  const [uploadingScontrino, setUploadingScontrino] = useState(false);
  const [showScontrinoChoice, setShowScontrinoChoice] = useState(false);
  const [prodottiNonDisponibili, setProdottiNonDisponibili] = useState([]);

  // Rinuncia ordine dopo accettazione
  const [showRinunciaModal, setShowRinunciaModal] = useState(false);
  const [rinunciaMotivoSelezionato, setRinunciaMotivoSelezionato] = useState('');
  const [rinunciaMotivoAltro, setRinunciaMotivoAltro] = useState('');
  const [rinunciaLoading, setRinunciaLoading] = useState(false);

  // Chat State
  const [chatMessages, setChatMessages] = useState([]);

  // Calendar State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [shifts, setShifts] = useState({});


  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const riderDoc = await getDocs(query(
            collection(db, 'riders'),
            where('uid', '==', user.uid)
          ));
          if (!riderDoc.empty) {
            const riderData = { id: riderDoc.docs[0].id, uid: user.uid, ...riderDoc.docs[0].data() };
            if (riderData.status !== 'pending') {
              setCurrentRider(riderData);
              setIsLoggedIn(true);
              // Ripristina ordine attivo se esiste (accettato O in consegna).
              // Prima cercava solo 'delivering': se il rider accettava e chiudeva
              // l'app prima di ritirare i prodotti, l'ordine 'accepted' spariva.
              const riderFirestoreId = riderDoc.docs[0].id;
              // La query di ripristino filtra per riderUid (UID Auth) per essere
              // allineata alle regole Firestore. Un solo where per NON richiedere
              // un indice composto: lo stato si filtra lato codice sui risultati.
              const activeOrderSnap = await getDocs(query(
                collection(db, 'orders'),
                where('riderUid', '==', user.uid)
              ));
              const activeOrderDoc = activeOrderSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .find(o => o.status === 'accepted' || o.status === 'delivering');
              if (activeOrderDoc) {
                const orderData = activeOrderDoc;
                const distance = orderData.calculatedDistance || '0.0';
                // Se l'ordine è già 'delivering' i prodotti sono stati ritirati
                setProdottiRitirati(orderData.status === 'delivering');
                if (orderData.scontrinoUrl) setScontrinoUrl(orderData.scontrinoUrl);
                setActiveOrder({
                  ...orderData,
                  distance: String(distance),
                  earning: (3 + parseFloat(distance) * 0.20).toFixed(2),
                  pharmacy: orderData.pharmacyName || 'Farmacia',
                  customer: orderData.customerName || 'Cliente',
                  address: orderData.deliveryAddress || 'Indirizzo non disponibile',
                  deliveryNotes: orderData.deliveryNotes || '',
                  itemsCount: orderData.items?.length || 0,
                  items: orderData.items || [],
                  total: orderData.total || 0,
                  ricettaUrl: orderData.ricettaUrl || null,
                  ricettaNome: orderData.ricettaNome || null,
                  customerLat: orderData.deliveryLat || 0,
                  customerLng: orderData.deliveryLng || 0
                });
                return;
              }

              // Ripristina ordine in popup (ready) se activeOrderId è presente sul rider
              const activeOrderId = riderData.activeOrderId;
              if (activeOrderId) {
                const pendingOrderSnap = await getDoc(doc(db, 'orders', activeOrderId));
                if (pendingOrderSnap.exists() && pendingOrderSnap.data().status === 'ready') {
                  showIncomingOrder({ id: pendingOrderSnap.id, ...pendingOrderSnap.data() });
                } else {
                  // Ordine non più valido — pulisci activeOrderId
                  await updateDoc(doc(db, 'riders', riderFirestoreId), { activeOrderId: null });
                }
              }
              return;
            }
          }
        } catch (error) {
          console.error('Errore caricamento rider:', error);
        }
        await signOut(auth);
        setCurrentRider(null);
        setIsLoggedIn(false);
      } else {
        setCurrentRider(null);
        setIsLoggedIn(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // OTTIENI POSIZIONE GPS RIDER
useEffect(() => {
  if (!isLoggedIn || !navigator.geolocation) return;
  
  let lastUpdate = 0;
  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      const now = Date.now();
      // Aggiorna lo stato React (e quindi la mappa) max ogni 7 secondi
      if (now - lastUpdate > 7000) {
        lastUpdate = now;
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        riderLocationRef.current = loc;
        setRiderLocation(loc);
      }
    },
    (error) => {
      console.log('GPS error:', error);
    },
    { enableHighAccuracy: true, maximumAge: 7000, timeout: 10000 }
  );
  
  return () => navigator.geolocation.clearWatch(watchId);
}, [isLoggedIn]);

// INIZIALIZZA FCM — usa Capacitor PushNotifications per notifiche native Android
useEffect(() => {
  if (!isLoggedIn || !currentRider?.id) return;

  const initFCM = async () => {
    try {
      // Controlla se siamo su Android nativo (Capacitor)
      const isNative = typeof window !== 'undefined' && 
                 window.Capacitor !== undefined && 
                 window.Capacitor.getPlatform() === 'android';

      if (isNative) {
        // FCM NATIVO — usa @capacitor/push-notifications
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Richiedi permesso
        const permResult = await PushNotifications.requestPermissions();
        if (permResult.receive !== 'granted') {
          console.log('Permesso notifiche negato');
          return;
        }

        // Registra per ricevere notifiche push
        await PushNotifications.register();

        // Ricevi token FCM
        PushNotifications.addListener('registration', async (token) => {
          console.log('FCM token nativo:', token.value);
          await updateDoc(doc(db, 'riders', currentRider.id), {
            fcmToken: token.value,
            fcmUpdatedAt: new Date().toISOString()
          });
        });

        // Notifica ricevuta con app in FOREGROUND
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Notifica foreground:', notification);
          playOrderSound();
          if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 500]);
        });

        // Click sulla notifica (app in background o chiusa)
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          console.log('Click notifica:', notification);
        });

        PushNotifications.addListener('registrationError', (error) => {
          console.error('Errore registrazione FCM:', error);
        });

      } else {
        // FCM WEB — fallback per browser/PWA
        if (!window.Notification) {
          console.log('Notifiche non supportate su questo dispositivo');
          return;
        }

        let permission = window.Notification.permission;
        if (permission === 'default') {
          permission = await window.Notification.requestPermission();
        }
        if (permission !== 'granted') {
          console.log('Permesso notifiche negato:', permission);
          return;
        }

        const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
        const messaging = getMessaging();

        const token = await getToken(messaging, {
          vapidKey: 'BLvztUjmKRJfT0sEIDNtbZMRRHEsJfvXXLH8khBa8IUQBwlteItHS2xBhCO9GrWJxbNx7l6y-EGoJv_Qx59gqcc'
        });

        if (token) {
          await updateDoc(doc(db, 'riders', currentRider.id), {
            fcmToken: token,
            fcmUpdatedAt: new Date().toISOString()
          });
          console.log('FCM token web salvato:', token);
        }

        onMessage(messaging, (payload) => {
          console.log('Notifica web foreground:', payload);
          playOrderSound();
          if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 500]);
          if (window.Notification.permission === 'granted') {
            new window.Notification(payload.notification?.title || 'Nuovo Ordine! 🛵', {
              body: payload.notification?.body || 'Hai un nuovo ordine da consegnare',
              icon: '/logo192.png'
            });
          }
        });
      }

    } catch (error) {
      console.error('Errore FCM:', error);
    }
  };

  initFCM();
}, [isLoggedIn, currentRider?.id]);

// CARICA TURNI DA FIRESTORE
useEffect(() => {
  if (!currentRider?.id) return;
  const unsubscribe = onSnapshot(doc(db, 'riders', currentRider.id), (docSnap) => {
    if (docSnap.exists()) {
      const data = docSnap.data();
      if (data.shifts) {
        setShifts(data.shifts);
      }
      // Ripristina stato online al riavvio dell'app
      if (typeof data.isOnline === 'boolean') {
        setIsOnline(data.isOnline);
      }
    }
  });
  return () => unsubscribe();
}, [currentRider?.id]);

// CONTROLLA DISCLAIMER GIORNALIERO
useEffect(() => {
  if (!isLoggedIn) return;
  const today = new Date().toISOString().split('T')[0];
  const lastAccepted = localStorage.getItem('disclaimer-accepted');
  if (lastAccepted !== today) {
    setShowDailyDisclaimer(true);
  }
}, [isLoggedIn]);

// CARICA ORDINI RIDER DA FIREBASE
useEffect(() => {
  if (!isLoggedIn || !currentRider?.id) return;
  
  // Query senza orderBy per NON richiedere un indice composto (riderUid + createdAt).
  // L'ordinamento per data si fa lato codice sui risultati ricevuti.
  const q = query(
    collection(db, 'orders'),
    where('riderUid', '==', currentRider.uid)
  );
  
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const orders = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    
    setCompletedOrders(orders.filter(o => o.status === 'delivered'));
    
    // Calcola guadagni totali
    const getEarning = (order) => 
      parseFloat(order.compensoRider) || parseFloat(order.earning) || (3 + (order.calculatedDistance || 0) * 0.20);

    const total = orders
      .filter(o => o.status === 'delivered')
      .reduce((sum, order) => sum + getEarning(order), 0);
    
    setTotalEarnings(total);
    setTotalEarningsFromDB(total);
    
    // Calcola guadagni oggi
    const today = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`;
    const todayTotal = orders
      .filter(o => o.status === 'delivered' && o.deliveredAt?.startsWith(today))
      .reduce((sum, order) => sum + getEarning(order), 0);
    
    setTodayEarnings(todayTotal);
    setTodayEarningsFromDB(todayTotal);
  });
  
  return () => unsubscribe();
}, [isLoggedIn, currentRider?.id]);

// SALVA POSIZIONE GPS IN FIRESTORE
// Scrive la posizione anche quando il rider è online SENZA ordine attivo:
// è necessario perché l'assegnazione filtra i rider entro 5km, e un rider
// senza posizione registrata verrebbe escluso.
useEffect(() => {
  if (!isLoggedIn || !currentRider) return;
  // Solo se il rider è online (in attesa di ordini o con ordine attivo)
  if (!isOnline && !activeOrder) return;

  const scriviPosizione = async () => {
    try {
      const loc = riderLocationRef.current;
      if (!loc || typeof loc.lat !== 'number' || typeof loc.lng !== 'number') return;
      await setDoc(doc(db, 'rider-positions', String(currentRider.id)), {
        riderId: currentRider.id,
        riderName: currentRider.name,
        orderId: activeOrder?.id || null,
        farmaciaId: activeOrder?.farmaciaId || null,
        lat: loc.lat,
        lng: loc.lng,
        updatedAt: serverTimestamp(),
        isActive: true
      });
    } catch (error) {
      console.error('Errore salvataggio GPS:', error);
    }
  };

  // Scrive subito (appena online) e poi ogni 7 secondi
  scriviPosizione();
  const interval = setInterval(scriviPosizione, 7000);

  return () => clearInterval(interval);
}, [isLoggedIn, isOnline, activeOrder, currentRider]); // scatta anche quando va online

// Comprime un'immagine lato client: ridimensiona e riduce la qualità JPEG,
// così l'upload resta ben sotto i 10MB anche con foto da fotocamera Android.
const comprimiImmagine = (file, maxLato = 1600, qualita = 0.7) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxLato || height > maxLato) {
          if (width > height) { height = Math.round(height * maxLato / width); width = maxLato; }
          else { width = Math.round(width * maxLato / height); height = maxLato; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => blob ? resolve(blob) : reject(new Error('Compressione fallita')),
          'image/jpeg', qualita
        );
      };
      img.onerror = () => reject(new Error('Immagine non valida'));
      img.src = ev.target.result;
    };
    reader.onerror = () => reject(new Error('Lettura file fallita'));
    reader.readAsDataURL(file);
  });
};

// Esegue l'upload effettivo di un file/blob scontrino
const eseguiUploadScontrino = async (file) => {
  if (!currentRider?.id) {
    alert('❌ Errore: rider non identificato. Chiudi e riapri l\'app.');
    return;
  }
  setUploadingScontrino(true);
  try {
    // Comprimi prima di caricare (best-effort: se fallisce, usa il file originale se < 10MB)
    let daCaricare = file;
    try {
      daCaricare = await comprimiImmagine(file);
    } catch (compErr) {
      console.warn('Compressione non riuscita, uso file originale:', compErr);
      if (file.size > 10 * 1024 * 1024) {
        alert('⚠️ La foto è troppo grande e non è stato possibile comprimerla. Riprova con una foto più piccola.');
        setUploadingScontrino(false);
        return;
      }
    }

    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const storage = getStorage();
    const storageRef = ref(storage, `scontrini/${currentRider.id}_${Date.now()}.jpg`);
    await uploadBytes(storageRef, daCaricare, { contentType: 'image/jpeg' });
    const url = await getDownloadURL(storageRef);
    setScontrinoUrl(url);
    await updateDoc(doc(db, 'orders', activeOrder.id), {
      scontrinoUrl: url,
      scontrinoCaricatoAt: new Date().toISOString()
    });
    alert('✅ Scontrino caricato!');
  } catch (err) {
    console.error('Errore upload scontrino:', err);
    const msg = err?.code === 'storage/unauthorized'
      ? 'Permesso negato. Verifica di essere loggato e riprova.'
      : (err?.message || 'errore sconosciuto');
    alert('❌ Errore caricamento: ' + msg);
  } finally {
    setUploadingScontrino(false);
  }
};

// Apre il selettore file. useCamera=true forza la fotocamera, false la galleria.
const apriSelettoreScontrino = (useCamera) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  if (useCamera) input.capture = 'environment';
  input.style.position = 'fixed';
  input.style.top = '-100px';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (input.parentNode) document.body.removeChild(input);
    if (!file) return;
    await eseguiUploadScontrino(file);
  });
  input.addEventListener('cancel', () => { if (input.parentNode) document.body.removeChild(input); });
  input.click();
};

// Mostra la scelta fotocamera / galleria
const uploadScontrino = () => {
  setShowScontrinoChoice(true);
};

// RIMUOVI POSIZIONE QUANDO CONSEGNA COMPLETATA
const clearRiderPosition = async () => {
  if (!currentRider) return;
  try {
    await setDoc(doc(db, 'rider-positions', String(currentRider.id)), {
      riderId: currentRider.id,
      isActive: false,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Errore rimozione GPS:', error);
  }
};



  // Refs per incomingOrder e activeOrder (usati nei listener)
  const incomingOrderRef = useRef(null);
  const activeOrderRef = useRef(null);
  const availableOrdersRef = useRef([]);
  useEffect(() => { incomingOrderRef.current = incomingOrder; }, [incomingOrder]);
  useEffect(() => { activeOrderRef.current = activeOrder; }, [activeOrder]);
  useEffect(() => { availableOrdersRef.current = availableOrders; }, [availableOrders]);

  // LISTENER PRINCIPALE — ascolta il documento rider su Firestore
  // Il server scrive activeOrderId quando assegna, null quando rifiuta/accetta
  // L'app non fa calcoli sulla coda — si fida solo del server come arbitro
  useEffect(() => {
    if (!isLoggedIn || !isOnline || !currentRider?.id) return;

    const riderDocRef = doc(db, 'riders', currentRider.id);

    const unsubscribeRider = onSnapshot(riderDocRef, async (riderSnap) => {
      if (!riderSnap.exists()) return;
      const riderData = riderSnap.data();
      const newActiveOrderId = riderData.activeOrderId;

      if (newActiveOrderId) {
        // Il server ha assegnato un ordine — scaricalo e mostra il popup
        // Skip se stiamo già mostrando questo ordine o se è già attivo
        if (incomingOrderRef.current?.id === newActiveOrderId) return;
        if (activeOrderRef.current?.id === newActiveOrderId) return;
        try {
          const orderSnap = await getDoc(doc(db, 'orders', newActiveOrderId));
          if (orderSnap.exists() && orderSnap.data().status === 'ready') {
            showIncomingOrder({ id: orderSnap.id, ...orderSnap.data() });
          }
        } catch (e) {
          console.error('Errore caricamento ordine assegnato:', e);
        }
      } else {
        // Il server ha rimosso activeOrderId — reset popup se aperto
        if (incomingOrderRef.current) {
          setIncomingOrder(null);
          setOrderTimer(25);
        }
      }
    }, (error) => {
      console.error('onSnapshot rider error:', error);
    });

    return () => unsubscribeRider();
  }, [isLoggedIn, isOnline, currentRider?.id]);

// Calcola distanza tra due coordinate (formula Haversine)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Raggio Terra in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return (R * c).toFixed(1); // Distanza in km
};

// SUONO NOTIFICA ORDINE (stile Glovo)
const playOrderSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    const playTone = (freq, startTime, duration, volume = 0.4) => {
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, startTime);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    // Sequenza sonora tipo Glovo: 3 beep ascendenti + pausa + 2 beep
    playTone(520, now + 0.00, 0.15, 0.5);
    playTone(660, now + 0.18, 0.15, 0.5);
    playTone(800, now + 0.36, 0.25, 0.6);
    playTone(660, now + 0.70, 0.15, 0.4);
    playTone(800, now + 0.88, 0.30, 0.6);
  } catch (e) {
    console.warn('Audio non disponibile:', e.message);
  }
};

  // Show incoming order popup
const showIncomingOrder = (order) => {
  let distance = '0.0';
  const riderLat = riderLocationRef.current.lat;
  const riderLng = riderLocationRef.current.lng;
  // Se GPS non disponibile usa centro Benevento come fallback
  const effectiveLat = (riderLat && riderLat !== 41.1297) ? riderLat : 41.1297;
  const effectiveLng = (riderLng && riderLng !== 14.7824) ? riderLng : 14.7824;
  if (order.deliveryLat && order.deliveryLng) {
    distance = calculateDistance(effectiveLat, effectiveLng, order.deliveryLat, order.deliveryLng);
  }
  
  const earning = (3 + parseFloat(distance) * 0.20).toFixed(2);
  
  setIncomingOrder({
    ...order,
    distance: distance,
    earning: earning,
    pharmacy: order.pharmacyName || 'Farmacia',
    customer: order.customerName || 'Cliente',
    address: order.deliveryAddress || 'Indirizzo non disponibile',
    deliveryNotes: order.deliveryNotes || '',
    itemsCount: order.items?.length || 0,
    items: order.items || [],
    total: order.total || 0,
    ricettaUrl: order.ricettaUrl || null,
    ricettaNome: order.ricettaNome || null,
    customerLat: order.deliveryLat || 0,
    customerLng: order.deliveryLng || 0
  });
  setOrderTimer(25);
  setShowNotification(true);
  setTimeout(() => setShowNotification(false), 3000);

  // Suono + vibrazione
  playOrderSound();
  if (navigator.vibrate) {
    navigator.vibrate([300, 100, 300, 100, 500]);
  }

  // Ripeti il suono ogni 3 secondi finché il popup è aperto
  const soundInterval = setInterval(() => {
    playOrderSound();
    if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
  }, 3000);
  setTimeout(() => clearInterval(soundInterval), 25000); // Stop dopo 25s (durata timer)
};

const handleRegister = async () => {
  if (!registerName.trim() || !registerEmail.trim() || !registerPassword.trim()) {
    setRegisterError('Compila tutti i campi');
    return;
  }
  if (registerPassword.length < 6) {
    setRegisterError('La password deve essere di almeno 6 caratteri');
    return;
  }
  setIsRegistering(true);
  setRegisterError('');
  const emailPulita = registerEmail.trim().toLowerCase();
  const nomeRider = registerName.trim();
  try {
    // 1. Crea l'utente Auth
    const userCredential = await createUserWithEmailAndPassword(auth, emailPulita, registerPassword);
    const uid = userCredential.user.uid;

    // 2. Collega l'uid al documento rider LATO SERVER (Cloud Function).
    //    La function collega SOLO se esiste una candidatura approvata dall'Admin.
    //    Se non esiste, restituisce 403: la registrazione non è consentita.
    let collegamentoOk = false;
    let messaggioErrore = '';
    try {
      const linkRes = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/collegaRiderUid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailPulita, uid: uid, name: nomeRider })
      });
      const linkData = await linkRes.json();
      if (linkRes.ok && linkData.success) {
        collegamentoOk = true;
      } else {
        messaggioErrore = linkData.error || 'Registrazione non consentita.';
      }
    } catch (linkErr) {
      console.warn('collegaRiderUid non riuscito:', linkErr);
      messaggioErrore = 'Errore di collegamento. Riprova più tardi.';
    }

    // 3. Se il collegamento NON è andato a buon fine (nessuna candidatura
    //    approvata), elimina l'account Auth appena creato e mostra l'errore.
    if (!collegamentoOk) {
      try {
        await userCredential.user.delete();
      } catch (delErr) {
        // Se non si riesce a eliminare, almeno fai signOut
        try { await signOut(auth); } catch (e) {}
      }
      setRegisterError(messaggioErrore);
      setIsRegistering(false);
      return;
    }

    // 4. Esci dalla sessione appena creata. onAuthStateChanged gestirà lo stato.
    try {
      await signOut(auth);
    } catch (signErr) {
      console.warn('signOut non riuscito (non bloccante):', signErr);
    }

    // 5. Mostra la schermata di successo
    setRegisterSuccess(true);
  } catch (error) {
    console.log('ERRORE:', error.code, error.message);
    if (error.code === 'auth/email-already-in-use') {
      setRegisterError('Questa email è già registrata. Prova ad accedere.');
    } else if (error.code === 'auth/invalid-email') {
      setRegisterError('Email non valida');
    } else if (error.code === 'auth/weak-password') {
      setRegisterError('Password troppo debole (min 6 caratteri)');
    } else {
      setRegisterError('Errore durante la registrazione. Riprova.');
    }
  } finally {
    setIsRegistering(false);
  }
};

  // Login Handler
  const handleLogin = async () => {
    if (!loginEmail.trim() || !loginPassword.trim()) {
      setLoginError('Inserisci email e password');
      return;
    }
    setIsLoading(true);
    setLoginError('');
    try {
      const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      const uid = userCredential.user.uid;
      // Cerca il rider in Firestore
      const riderDoc = await getDocs(query(
        collection(db, 'riders'),
        where('uid', '==', uid)
      ));
      if (riderDoc.empty) {
        await signOut(auth);
        setLoginError('Account non trovato. Contatta il supporto.');
        setIsLoading(false);
        return;
      }
      const riderData = { id: riderDoc.docs[0].id, uid: user.uid, ...riderDoc.docs[0].data() };
      if (riderData.status === 'pending') {
        await signOut(auth);
        setLoginError('Account in attesa di approvazione. Ti contatteremo presto.');
        setIsLoading(false);
        return;
      }
      setCurrentRider(riderData);
      setIsLoggedIn(true);
    } catch (error) {
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setLoginError('Email o password errati');
      } else {
        setLoginError('Errore durante il login');
      }
    }
    setIsLoading(false);
  };

  // Logout Handler
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setCurrentRider(null);
      setIsOnline(false);
      setActiveOrder(null);
      setIncomingOrder(null);
      setCurrentView('map');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Shift Management
  const toggleShift = async (date, period) => {
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const currentShifts = shifts[dateStr] || { morning: false, afternoon: false };

  if (currentShifts[period]) {
    alert('⚠️ Impossibile annullare il turno. Contatta il supporto per modifiche.');
    return;
  }

  const newShifts = {
    ...shifts,
    [dateStr]: {
      ...currentShifts,
      [period]: true
    }
  };

  setShifts(newShifts);

  try {
    await updateDoc(doc(db, 'riders', currentRider.id), {
      shifts: newShifts
    });
  } catch (error) {
    console.error('Errore salvataggio turno:', error);
  }
};

  // Order Timer
  useEffect(() => {
    if (incomingOrder && orderTimer > 0) {
      const timer = setTimeout(() => setOrderTimer(orderTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else if (incomingOrder && orderTimer === 0) {
      // Timer scaduto — passa l'ordine al rider successivo
      const ordineScaduto = incomingOrder;
      setIncomingOrder(null);
      setOrderTimer(25);
      // Registra il rifiuto su Firestore — l'ordine resta fermo (logica pacata)
      (async () => {
        try {
          // Timer scaduto: aggiungi rider a riderGiaNotificati (array semplice, NO timestamp)
          await updateDoc(doc(db, 'orders', ordineScaduto.id), {
            assignedRiderId: null,
            assignedRiderName: null,
            riderGiaNotificati: arrayUnion(currentRider?.id)
          });
          // Pulisci activeOrderId sul rider — torna libero per altri ordini
          if (currentRider?.id) {
            await updateDoc(doc(db, 'riders', currentRider.id), { activeOrderId: null });
          }
          // LOGICA PACATA: NON chiamiamo assegnaRider — l'ordine resta fermo
          // fino al prossimo giro dello scheduler (max 2 min). Il telefono respira.
        } catch(e) {
          console.warn('Errore reset ordine scaduto:', e);
        }
      })();
    }
  }, [incomingOrder, orderTimer]);

  // Accept Order
  const acceptOrder = async () => {
    try {
    // Update order status to accepted (va a 'delivering' solo quando il rider clicca Prodotti Ritirati)
    await updateDoc(doc(db, 'orders', incomingOrder.id), {
      status: 'accepted',
      riderId: currentRider.id,
      riderName: currentRider.name,
      acceptedAt: new Date().toISOString(),
      calculatedDistance: parseFloat(incomingOrder.distance) || 0
    });

    setActiveOrder({
      ...incomingOrder,
      paymentMethod: incomingOrder.paymentMethod || 'cash',
    });
    setChatMessages([]);
    setIncomingOrder(null);
    setOrderTimer(25);

    // LOGICA DOPPIA CONSEGNA
    // Controlla se ci sono condizioni per doppia consegna
    const totalAvailableOrders = availableOrders.filter(o => o.id !== incomingOrder.id).length;
    const onlineRiders = 2; // TODO: da Firebase - numero rider online
    
    if (totalAvailableOrders >= 5 || onlineRiders <= 2) {
      // Cerca secondo ordine vicino (max 2km)
      const riderLat = 40.8518;
      const riderLng = 14.2681;
      
      const nearbyOrders = availableOrders
        .filter(order => order.id !== incomingOrder.id)
        .filter(order => {
          if (!order.pharmacyLat || !order.pharmacyLng) return false;
          const distance = parseFloat(calculateDistance(
            riderLat, riderLng, 
            order.pharmacyLat, order.pharmacyLng
          ));
          return distance <= 2; // Max 2km
        });

      if (nearbyOrders.length > 0) {
        const secondOrder = nearbyOrders[0];
        const bonus = 2.50;
        
        // Mostra notifica doppia consegna
        if (window.confirm(
          `🎉 DOPPIA CONSEGNA DISPONIBILE!\n\n` +
          `Ordine: ${secondOrder.pharmacy?.name || 'Farmacia'}\n` +
          `Distanza: ${secondOrder.calculatedDistance || '0.0'} km\n` +
          `Bonus: +€${bonus.toFixed(2)}\n\n` +
          `Vuoi accettare anche questo ordine?`
        )) {
          // Accetta secondo ordine
          await updateDoc(doc(db, 'orders', secondOrder.id), {
            status: 'accepted',
            riderId: currentRider.id,
            riderName: currentRider.name,
            acceptedAt: new Date().toISOString(),
            isDoubleDelivery: true,
            doubleDeliveryBonus: bonus
          });
          
          alert(`✅ Doppia consegna accettata! Bonus: +€${bonus.toFixed(2)}`);
        }
      }
    }

  } catch (error) {
    console.error('Error accepting order:', error);
    alert('Errore durante l\'accettazione dell\'ordine');
  }
};

  // Complete Order
  const completeOrder = async () => {
    try {
      // Calcola km percorsi: farmacia → cliente
      let deliveryDistanceKm = 0;
      if (activeOrder.deliveryLat && activeOrder.deliveryLng) {
        const rLat = riderLocationRef.current.lat;
        const rLng = riderLocationRef.current.lng;
        deliveryDistanceKm = parseFloat(calculateDistance(
          rLat, rLng,
          activeOrder.deliveryLat, activeOrder.deliveryLng
        ));
      } else if (activeOrder.calculatedDistance) {
        deliveryDistanceKm = parseFloat(activeOrder.calculatedDistance);
      }

      // Calcola compenso rider
      const compensoBase = 3.00;
      const compensoKm = deliveryDistanceKm * 0.20;
      const isFestivo = [0, 6].includes(new Date().getDay());
      const subtotale = compensoBase + compensoKm;
      const maggiorazione = isFestivo ? subtotale * 0.20 : 0;
      const compensoTotale = subtotale + maggiorazione;

      // Dati contabili
      // La RICETTA è a pagamento alla consegna: il rider incassa dal cliente
      // (contanti), NON viene rimborsato, e la quota FarmaciaSubito (€4,99) gli
      // viene scalata dal compenso in liquidazione.
      const isRicetta = activeOrder.isRicettaOrder === true;
      const isContanti = activeOrder.paymentMethod === 'cash' || isRicetta;

      const importoDigitato = parseFloat(importoPagato) || 0;
      // Anticipo farmacia (quanto ha speso il rider per il farmaco/prodotti)
      const farmaciAnticipati = isRicetta
        ? importoDigitato
        : (activeOrder.subtotal || activeOrder.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0);

      // Quota FarmaciaSubito che il rider incassa dal cliente per conto nostro.
      // Ricetta: è la deliveryFee (€4,99). Ordine contanti normale: totale - anticipo.
      const quotaFarmaciaSubito = isRicetta
        ? (activeOrder.deliveryFee || 4.99)
        : (isContanti ? parseFloat(((activeOrder.total || 0) - farmaciAnticipati).toFixed(2)) : 0);

      // Totale che il rider ha in mano: ricetta = anticipo + quota FS; contanti = total ordine
      const totaleIncassato = isRicetta
        ? parseFloat((farmaciAnticipati + quotaFarmaciaSubito).toFixed(2))
        : (isContanti ? (activeOrder.total || 0) : 0);

      // Compenso netto del rider per questa consegna (può essere negativo se la
      // quota incassata per FS supera il compenso).
      const compensoNetto = parseFloat((compensoTotale - quotaFarmaciaSubito).toFixed(2));

      // Update order status to delivered
      await updateDoc(doc(db, 'orders', activeOrder.id), {
        status: 'delivered',
        deliveredAt: new Date().toISOString(),
        deliveryDistanceKm: deliveryDistanceKm,
        compensoRider: compensoTotale,
        compensoBase: compensoBase,
        compensoKm: compensoKm,
        maggiorazioneFestiva: maggiorazione,
        isFestivo: isFestivo,
        importoPagatoFarmacia: importoDigitato,
        scontrinoUrl: scontrinoUrl || null,
        // Contabilità
        pagamentoContanti: isContanti,
        isRicettaContanti: isRicetta,
        contattiIncassati: totaleIncassato,
        farmaciAnticipati: farmaciAnticipati,
        quotaFarmaciaSubito: quotaFarmaciaSubito,
        costoConsegnaIncassato: quotaFarmaciaSubito,
        compensoNetto: compensoNetto,
        // In liquidazione si scala al rider la quota FarmaciaSubito incassata in contanti.
        contanti_daScalareInLiquidazione: quotaFarmaciaSubito,
        // Rimborso rider: SOLO per ordini con carta (non ricetta, non contanti),
        // dove il rider ha anticipato di tasca propria e carica lo scontrino.
        rimborsoRiderStatus: isContanti ? 'non_dovuto' : 'da_rimborsare',
        rimborsoRiderImporto: isContanti ? 0 : farmaciAnticipati,
      });

      
      setCompletedOrders([...completedOrders, {
        ...activeOrder,
        completedAt: new Date().toISOString()
      }]);

      // Riepilogo compenso al rider (per ricetta/contanti dove incassa la quota FS)
      if (quotaFarmaciaSubito > 0) {
        const segno = compensoNetto < 0 ? '-' : '';
        alert(
          `✅ Consegna completata!\n\n` +
          `📊 Riepilogo compenso:\n` +
          `• Compenso consegna: €${compensoTotale.toFixed(2)}\n` +
          `• Hai incassato in contanti: €${totaleIncassato.toFixed(2)}\n` +
          `   di cui quota FarmaciaSubito: €${quotaFarmaciaSubito.toFixed(2)}\n\n` +
          `Compenso netto per questa consegna: ${segno}€${Math.abs(compensoNetto).toFixed(2)}\n\n` +
          (compensoNetto < 0
            ? `Nota: la quota FarmaciaSubito che hai incassato supera il compenso, quindi questa differenza verrà conguagliata nella liquidazione mensile.`
            : `Verrà conteggiato nella liquidazione mensile.`)
        );
      } else {
        alert('✅ Consegna completata!');
      }

      setActiveOrder(null);
      setProdottiRitirati(false);
      setScontrinoUrl(null);
      setImportoPagato('');
      clearRiderPosition();
      setChatMessages([]);
    } catch (error) {
      console.error('Error completing order:', error);
      alert('Errore durante il completamento dell\'ordine');
    }
  };

  // Rinuncia ordine dopo accettazione (solo FASE 1 — prima di ritirare i prodotti)
  const rinunciaOrdine = async () => {
    const motivoFinale = rinunciaMotivoSelezionato === 'Altro'
      ? rinunciaMotivoAltro.trim()
      : rinunciaMotivoSelezionato;

    if (!motivoFinale) {
      alert('⚠️ Seleziona o inserisci un motivo per la rinuncia.');
      return;
    }

    setRinunciaLoading(true);
    try {
      const ordineId = activeOrder.id;

      // La rinuncia è gestita lato server (Cloud Function) per affidabilità:
      // sgancia il rider, rimette l'ordine in circolo e richiama l'assegnazione.
      const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/rinunciaConsegna', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: ordineId,
          riderId: currentRider.id,
          riderName: currentRider.name,
          motivo: motivoFinale
        })
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Errore rinuncia');

      // Reset stato locale — il rider RESTA online (non chiamiamo clearRiderPosition)
      setActiveOrder(null);
      setProdottiRitirati(false);
      setScontrinoUrl(null);
      setImportoPagato('');
      setShowRinunciaModal(false);
      setRinunciaMotivoSelezionato('');
      setRinunciaMotivoAltro('');
      setRinunciaLoading(false);
      setChatMessages([]);
    } catch (error) {
      console.error('Errore rinuncia ordine:', error);
      alert('\u274c Errore durante la rinuncia. Riprova.');
      setRinunciaLoading(false);
    }
  };



  // LOGIN VIEW
if (!isLoggedIn) {
  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="relative inline-block mb-4">
            <img 
              src="/MARCHIO_FARMACIASUBITO.jpg"
              alt="FarmaciaSubito" 
              className="h-28 w-auto object-contain"
            />
            <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full shadow-lg">
              <span className="text-xs font-black">RIDER</span>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Portale Rider</h1>
          <p className="text-gray-500 mt-2">Gestisci le tue consegne</p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="rider@example.com"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
            />
          </div>
          
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
            <input
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="••••••••"
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
            />
          </div>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {loginError}
            </div>
          )}

          <button
            onClick={handleLogin}
            disabled={isLoading}
            className="w-full bg-orange-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-orange-700 transition shadow-lg disabled:bg-gray-400"
          >
            {isLoading ? 'Caricamento...' : 'Accedi'}
          </button>

          <button
            onClick={() => { setShowRegister(true); setLoginError(''); }}
            className="w-full text-center text-sm text-orange-600 font-semibold hover:underline mt-2"
          >
            Non hai un account? Registrati
          </button>
        </div>
      </div>
    </div>

    {/* MODAL REGISTRAZIONE */}
    {showRegister && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-2xl">
          {registerSuccess ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-600" size={32} />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Registrazione inviata!</h3>
              <p className="text-gray-500 text-sm mb-6">Il tuo account è in attesa di approvazione. Ti contatteremo via email quando sarà attivato.</p>
              <button
                onClick={() => { setShowRegister(false); setRegisterSuccess(false); setRegisterName(''); setRegisterEmail(''); setRegisterPassword(''); }}
                className="w-full bg-orange-600 text-white py-3 rounded-lg font-bold hover:bg-orange-700 transition"
              >
                Torna al login
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Registrati come Rider</h3>
                <button onClick={() => { setShowRegister(false); setRegisterError(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Nome completo</label>
                  <input
                    type="text"
                    value={registerName}
                    onChange={(e) => setRegisterName(e.target.value)}
                    placeholder="Mario Rossi"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={registerEmail}
                    onChange={(e) => setRegisterEmail(e.target.value)}
                    placeholder="mario@example.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={registerPassword}
                    onChange={(e) => setRegisterPassword(e.target.value)}
                    placeholder="Minimo 6 caratteri"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-orange-500 outline-none"
                  />
                </div>
                {registerError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {registerError}
                  </div>
                )}
                <button
                  onClick={handleRegister}
                  disabled={isRegistering}
                  className="w-full bg-orange-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-orange-700 transition shadow-lg disabled:bg-gray-400"
                >
                  {isRegistering ? 'Registrazione...' : 'Registrati'}
                </button>
                <p className="text-xs text-gray-400 text-center">Dopo la registrazione il tuo account verrà verificato dal team FarmaciaSubito prima dell'attivazione.</p>
              </div>
             </>
          )}
        </div>
      </div>
      )}
    </>
  );
}        

  // MAP VIEW
  const MapView = () => (
    <div className="space-y-4 pb-24">

      {/* BANNER ATTIVA NOTIFICHE — appare se notifiche non autorizzate */}
      {window.Notification && window.Notification.permission !== 'granted' && (
        <div className="bg-orange-50 border-2 border-orange-400 rounded-xl p-3 flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-black text-orange-800">🔔 Attiva le notifiche</p>
            <p className="text-xs text-orange-600">Necessarie per ricevere i nuovi ordini</p>
          </div>
          <button
            onClick={async () => {
              try {
                const permission = await window.Notification.requestPermission();
                if (permission === 'granted') {
                  const { getMessaging, getToken } = await import('firebase/messaging');
                  const messaging = getMessaging();
                  const token = await getToken(messaging, {
                    vapidKey: 'BLvztUjmKRJfT0sEIDNtbZMRRHEsJfvXXLH8khBa8IUQBwlteItHS2xBhCO9GrWJxbNx7l6y-EGoJv_Qx59gqcc'
                  });
                  if (token) {
                    await updateDoc(doc(db, 'riders', currentRider.id), {
                      fcmToken: token,
                      fcmUpdatedAt: new Date().toISOString()
                    });
                  }
                  alert('✅ Notifiche attivate!');
                } else {
                  alert('⚠️ Permesso negato. Vai nelle impostazioni del telefono per attivare le notifiche.');
                }
              } catch(e) {
                console.error('Errore attivazione notifiche:', e);
              }
            }}
            className="bg-orange-600 text-white px-3 py-2 rounded-lg font-bold text-sm whitespace-nowrap"
          >
            Attiva
          </button>
        </div>
      )}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Stato</h2>
            <p className="text-sm text-gray-500">
              {currentShift ? `Turno: ${currentShift}` : 'Nessun turno attivo'}
            </p>
          </div>
          <button
  onClick={async () => {
    if (!isOnline) {
      // Controlla se c'è un turno attivo oggi
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      const todayShifts = shifts[todayStr] || { morning: false, afternoon: false };
      const hour = now.getHours();
      const minutes = now.getMinutes();
      const hasMorningShift = todayShifts.morning && hour >= 9 && hour < 13;
      const hasAfternoonShift = todayShifts.afternoon && hour >= 16 && (hour < 20 || (hour === 20 && minutes <= 30));

      if (!hasMorningShift && !hasAfternoonShift) {
        alert('⚠️ Non hai un turno attivo adesso. Prenota un turno nel calendario per andare online.');
        return;
      }
    } else {
      // Blocca offline durante turno attivo
      const now = new Date();
      const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
      const todayShifts = shifts[todayStr] || { morning: false, afternoon: false };
      const hour = now.getHours();
      const minutes = now.getMinutes();
      const hasMorningShift = todayShifts.morning && hour >= 9 && (hour < 13 || (hour === 13 && minutes === 0));
      const hasAfternoonShift = todayShifts.afternoon && hour >= 16 && (hour < 20 || (hour === 20 && minutes <= 30));

      if (hasMorningShift || hasAfternoonShift) {
        alert('⚠️ Non puoi andare offline durante il turno. Il turno termina alle ' + (hasMorningShift ? '13:00' : '20:30') + '.');
        return;
      }
    }
    const newOnlineState = !isOnline;   // <-- da qui in poi
    setIsOnline(newOnlineState);
    try {
      await updateDoc(doc(db, 'riders', currentRider.id), {
        isOnline: newOnlineState,
        isOnlineUpdatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Errore salvataggio stato online:', error);
    }
  }}
  className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition shadow-lg ${
    isOnline
      ? 'bg-green-600 text-white hover:bg-green-700'
      : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
  }`}
>
  <Power size={20} />
  {isOnline ? 'Online' : 'Offline'}
</button>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <DollarSign className="text-green-600" size={20} />
            <span className="text-sm text-gray-600">Guadagno oggi:</span>
          </div>
          <span className="text-xl font-bold text-green-600">€{todayEarnings.toFixed(2)}</span>
        </div>
      </div>

      <div className="rounded-xl overflow-hidden shadow-md" style={{ height: '400px', position: 'relative', zIndex: 0 }}>
  <LeafletMap riderLocation={riderLocation} activeOrder={activeOrder} />
</div>
      {activeOrder && (
        <div className="bg-white p-4 rounded-xl shadow-md border-2 border-green-500">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-bold text-lg">Ordine in corso</h3>
              <p className="text-xs text-gray-400 font-mono">#{activeOrder.id?.substring(0, 8)}</p>
            </div>
            <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-semibold">
              In consegna
            </span>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <MapPin className="text-green-600" size={20} />
              <div>
                <p className="font-semibold text-gray-800">{activeOrder.customer}</p>
                <p className="text-sm text-gray-500">{activeOrder.address}</p>
              </div>
            </div>

            {activeOrder.deliveryNotes && activeOrder.deliveryNotes.trim() !== '' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-black text-amber-800 mb-1">📝 Note del cliente</p>
                <p className="text-sm text-amber-900">{activeOrder.deliveryNotes}</p>
              </div>
            )}
            
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Navigation size={16} className="text-gray-600" />
                <span>{activeOrder.distance} km</span>
              </div>
              <div className="flex items-center gap-1">
                <Package size={16} className="text-gray-600" />
                <span>{activeOrder.itemsCount || activeOrder.items?.length || 0} prodotti</span>
              </div>
              <div className="flex items-center gap-1 text-green-600 font-semibold">
                <DollarSign size={16} />
                <span>€{activeOrder.earning}</span>
              </div>
            </div>
          </div>

          {/* CODICE FISCALE ALERT */}
          {activeOrder?.codiceFiscale && activeOrder.codiceFiscale.trim() !== '' && !prodottiRitirati && (
            <div className="mt-3 bg-yellow-50 border-2 border-yellow-400 rounded-lg p-3 flex items-center gap-3">
              <span className="text-2xl flex-shrink-0">⚠️</span>
              <div>
                <p className="text-xs font-black text-yellow-800">CODICE FISCALE DA FORNIRE AL FARMACISTA</p>
                <p className="text-lg font-black text-yellow-900 tracking-widest">{activeOrder.codiceFiscale}</p>
              </div>
            </div>
          )}

          {/* PRODOTTI DA ACQUISTARE */}
          {activeOrder.items && activeOrder.items.length > 0 && (
            <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-xs font-bold text-gray-600 mb-2">🛒 Prodotti da acquistare in farmacia:</p>
              {activeOrder.items.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt={item.name}
                      className="w-12 h-12 rounded-lg object-cover border border-gray-200 flex-shrink-0 bg-white"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0 text-lg">
                      💊
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 font-semibold leading-tight">{item.name}</p>
                    <p className="text-xs text-gray-500">Quantità: {item.quantity}</p>
                  </div>
                  <span className="text-gray-600 text-sm font-semibold flex-shrink-0">€{(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {/* IMPORTI */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
              <p className="text-xs text-orange-600 font-bold">💊 Paga in farmacia</p>
              <p className="text-lg font-black text-orange-700">€{(activeOrder.subtotal || activeOrder.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0).toFixed(2)}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-xs text-green-600 font-bold">💵 Riscuoti dal cliente</p>
              <p className="text-lg font-black text-green-700">
                {activeOrder.paymentMethod === 'cash' ? `€${(activeOrder.total || 0).toFixed(2)}` : '✅ Già pagato'}
              </p>
            </div>
          </div>

          {!prodottiRitirati ? (
            // FASE 1: In farmacia
            <div className="space-y-2 mt-4">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => {
                    const lat = activeOrder?.pharmacyLat || 41.1297;
                    const lng = activeOrder?.pharmacyLng || 14.7824;
                    window.open(`https://maps.google.com/?q=${lat},${lng}`);
                  }}
                  className="bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-1"
                >
                  <Navigation size={16} />
                  Vai in Farmacia
                </button>
                <button
                  onClick={() => setCurrentView('chat')}
                  className="bg-gray-600 text-white py-2 rounded-lg font-semibold hover:bg-gray-700 transition flex items-center justify-center gap-1"
                >
                  <MessageCircle size={16} />
                  Chat
                </button>
              </div>
              {activeOrder?.paymentMethod !== 'cash' && (
                <div className="bg-blue-50 border-2 border-blue-400 rounded-xl p-3">
                  <p className="text-sm font-black text-blue-800">
                    💳 Paga con la tua carta personale
                  </p>
                  <p className="text-lg font-black text-blue-900 mt-1">
                    Importo da pagare in farmacia: €{(activeOrder.subtotal || activeOrder.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0).toFixed(2)}
                  </p>
                </div>
              )}

              {/* SCONTRINO — obbligatorio SOLO per ordini pagati con carta (il rider
                  anticipa e serve prova per il rimborso). Per i contanti il rider non
                  anticipa nulla: lo scontrino è facoltativo e NON blocca il ritiro. */}
              {(() => {
                const isCash = activeOrder?.paymentMethod === 'cash';
                const scontrinoObbligatorio = !isCash;
                const puoRitirare = !scontrinoObbligatorio || !!scontrinoUrl;
                return (
                  <>
                    <div className={`rounded-xl p-3 space-y-2 border-2 ${
                      scontrinoUrl ? 'bg-green-50 border-green-400'
                        : scontrinoObbligatorio ? 'bg-red-50 border-red-500'
                        : 'bg-gray-50 border-gray-300'
                    }`}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{scontrinoUrl ? '✅' : '🧾'}</span>
                        <p className={`text-sm font-black ${
                          scontrinoUrl ? 'text-green-800'
                            : scontrinoObbligatorio ? 'text-red-700' : 'text-gray-700'
                        }`}>
                          {scontrinoUrl
                            ? 'Scontrino caricato'
                            : scontrinoObbligatorio
                              ? 'Carica lo scontrino della farmacia'
                              : 'Scontrino (facoltativo per contanti)'}
                        </p>
                      </div>
                      {!scontrinoUrl && scontrinoObbligatorio && (
                        <p className="text-xs font-semibold text-red-600">
                          Obbligatorio: paga in farmacia, fotografa lo scontrino e caricalo qui prima di proseguire.
                        </p>
                      )}
                      {scontrinoUrl ? (
                        <button
                          onClick={() => window.open(scontrinoUrl, '_blank')}
                          className="w-full bg-white border border-green-300 text-green-700 py-2 rounded-lg font-bold text-sm hover:bg-green-100 transition"
                        >
                          👁️ Vedi scontrino caricato
                        </button>
                      ) : (
                        <button
                          onClick={uploadScontrino}
                          disabled={uploadingScontrino}
                          className={`w-full text-white py-2 rounded-lg font-bold text-sm transition disabled:bg-gray-400 flex items-center justify-center gap-2 ${
                            scontrinoObbligatorio ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-500 hover:bg-gray-600'
                          }`}
                        >
                          {uploadingScontrino ? (
                            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Caricamento...</>
                          ) : (
                            '📸 Carica Scontrino'
                          )}
                        </button>
                      )}
                    </div>

                    <button
                      onClick={async () => {
                        // BLOCCO scontrino solo se obbligatorio (ordini carta)
                        if (scontrinoObbligatorio && !scontrinoUrl) {
                          alert('⚠️ Carica prima lo scontrino della farmacia. È obbligatorio per gli ordini pagati con carta.');
                          return;
                        }
                        if (activeOrder?.codiceFiscale && activeOrder.codiceFiscale.trim() !== '') {
                          setCodiceFiscaleStep(1);
                          setShowCodiceFiscalePopup(true);
                        } else {
                          // Aggiorna status a delivering solo ora
                          await updateDoc(doc(db, 'orders', activeOrder.id), { status: 'delivering' });
                          setProdottiRitirati(true);
                        }
                      }}
                      disabled={!puoRitirare}
                      className={`w-full py-3 rounded-lg font-black transition flex items-center justify-center gap-2 ${
                        puoRitirare
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      <Check size={18} />
                      {puoRitirare ? '✅ Prodotti Ritirati — Vai dal Cliente' : '🔒 Carica lo scontrino per continuare'}
                    </button>
                  </>
                );
              })()}
              <button
                onClick={() => {
                  setRinunciaMotivoSelezionato('');
                  setRinunciaMotivoAltro('');
                  setShowRinunciaModal(true);
                }}
                className="w-full bg-white border-2 border-red-400 text-red-600 py-2 rounded-lg font-semibold hover:bg-red-50 transition flex items-center justify-center gap-2 text-sm"
              >
                <X size={16} />
                Rinuncia all'ordine
              </button>
            </div>
          ) : (
            // FASE 2: In consegna al cliente
            <div className="space-y-2 mt-4">
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => {
                    const lat = activeOrder?.deliveryLat || activeOrder?.customerLat || 0;
                    const lng = activeOrder?.deliveryLng || activeOrder?.customerLng || 0;
                    if (lat && lng) {
                      window.open(`https://maps.google.com/?q=${lat},${lng}`);
                    } else {
                      alert('❌ Coordinate cliente non disponibili!');
                    }
                  }}
                  className="bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-1"
                >
                  <Navigation size={16} />
                  Naviga
                </button>
                {activeOrder?.customerPhone && (
                  <a href={`tel:${activeOrder.customerPhone}`}
                    className="bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-1">
                    <Phone size={16} />
                    Chiama
                  </a>
                )}
                <button
                  onClick={() => setCurrentView('chat')}
                  className="bg-gray-600 text-white py-2 rounded-lg font-semibold hover:bg-gray-700 transition flex items-center justify-center gap-1"
                >
                  <MessageCircle size={16} />
                  Chat
                </button>
              </div>
              {/* SCONTRINO già caricato in FASE 1. Qui solo l'importo per ordini ricetta. */}
              {activeOrder?.isRicettaOrder && (
                <ImportoRicettaInput
                  valoreIniziale={importoPagato}
                  onCommit={setImportoPagato}
                  scontrinoUrl={scontrinoUrl}
                />
              )}
              <button
                onClick={() => {
                  if (activeOrder?.isRicettaOrder && (!importoPagato || isNaN(parseFloat(importoPagato)) || parseFloat(importoPagato) <= 0)) {
                    alert('⚠️ Inserisci l\'importo che hai pagato in farmacia (es. 12.50).');
                    return;
                  }
                  // Conferma esplicita prima di chiudere la consegna
                  if (window.confirm('Confermi di aver portato a termine la consegna?')) {
                    completeOrder();
                  }
                }}
                className="w-full bg-green-600 text-white py-3 rounded-lg font-black hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                <Check size={18} />
                ✅ Consegna Completata
              </button>
            </div>
          )}

          {/* RICETTA */}
          {activeOrder?.ricettaUrl && activeOrder.ricettaUrl !== 'null' && activeOrder.ricettaUrl !== '' && (
            <div className="w-full mt-2 bg-blue-50 border-2 border-blue-300 rounded-xl p-3">
              <p className="text-xs font-black text-blue-800 mb-2 flex items-center gap-1">
                📋 Ricetta del Cliente — mostrala al farmacista per la scansione
              </p>
              <div
                onClick={() => setRicettaFullscreen(activeOrder.ricettaUrl)}
                className="cursor-pointer rounded-lg overflow-hidden border border-blue-200 bg-white"
              >
                <img
                  src={activeOrder.ricettaUrl}
                  alt="Ricetta cliente"
                  className="w-full object-contain max-h-80"
                  style={{ backgroundColor: '#fff' }}
                />
              </div>
              <button
                onClick={() => setRicettaFullscreen(activeOrder.ricettaUrl)}
                className="w-full mt-2 bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 text-sm"
              >
                🔍 Ingrandisci a schermo intero
              </button>
            </div>
          )}

          {/* PRODOTTO NON TROVATO */}
          <button
            onClick={() => setShowProdottoNonTrovato(true)}
            className="w-full mt-2 bg-red-50 border border-red-200 text-red-600 py-2 rounded-lg font-semibold hover:bg-red-100 transition flex items-center justify-center gap-2 text-sm"
          >
            <X size={16} />
            Prodotto non disponibile in farmacia
          </button>
        </div>
      )}

      {!activeOrder && isOnline && availableOrders.length === 0 && (
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl text-center">
          <AlertCircle className="text-blue-600 mx-auto mb-2" size={32} />
          <p className="text-blue-800 font-semibold">In attesa di nuovi ordini...</p>
          <p className="text-sm text-blue-600 mt-1">Riceverai una notifica quando arriva un ordine</p>
        </div>
      )}

      {!isOnline && (
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl text-center">
          <Power className="text-gray-400 mx-auto mb-2" size={32} />
          <p className="text-gray-600 font-semibold">Sei offline</p>
          <p className="text-sm text-gray-500 mt-1">Attiva lo stato online per ricevere ordini</p>
        </div>
      )}
    </div>
  );

  // CALENDAR VIEW
  const CalendarView = () => {
    const today = new Date();
    const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).getDay();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return (
      <div className="space-y-4 pb-24">
        <div className="bg-white p-4 rounded-xl shadow-md">
          <h2 className="text-xl font-bold mb-4">Calendario Turni</h2>
          
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['D', 'L', 'M', 'M', 'G', 'V', 'S'].map(day => (
              <div key={day} className="text-center font-semibold text-gray-600 text-sm">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              if (!day) return <div key={index} />;
              
              const date = new Date(today.getFullYear(), today.getMonth(), day);
              const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const dayShifts = shifts[dateStr] || { morning: false, afternoon: false };
              const hasShift = dayShifts.morning || dayShifts.afternoon;
              
              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  className={`aspect-square rounded-lg flex flex-col items-center justify-center text-sm transition ${
                    hasShift
                      ? 'bg-green-100 border-2 border-green-500 text-green-800 font-bold'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {day}
                  {hasShift && (
                    <div className="flex gap-0.5 mt-1">
                      {dayShifts.morning && <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />}
                      {dayShifts.afternoon && <div className="w-1.5 h-1.5 bg-green-600 rounded-full" />}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl shadow-md">
          <h3 className="font-bold mb-3">
            Turni per {selectedDate.toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}
          </h3>
          
          <div className="space-y-3">
            {[
              { id: 'morning', label: 'Mattina', time: '09:00 - 13:00' },
              { id: 'afternoon', label: 'Pomeriggio', time: '16:00 - 20:30' }
            ].map(shift => {
              const d = selectedDate;
              const dateStr = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const isSelected = shifts[dateStr]?.[shift.id] || false;
              
              return (
                <button
                  key={shift.id}
                  onClick={() => toggleShift(selectedDate, shift.id)}
                  className={`w-full p-4 rounded-lg border-2 transition ${
                    isSelected
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="font-semibold text-gray-800">{shift.label}</p>
                      <p className="text-sm text-gray-500">{shift.time}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      isSelected ? 'border-green-600 bg-green-600' : 'border-gray-300'
                    }`}>
                      {isSelected && <Check className="text-white" size={16} />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              ⚠️ Una volta confermato un turno, non è possibile annullarlo. Contatta il supporto per modifiche.
            </p>
          </div>
        </div>
      </div>
    );
  };

// EARNINGS VIEW
const EarningsView = () => {
  const getFilteredOrders = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    
    return completedOrders.filter(order => {
      const orderDate = new Date(order.deliveredAt || order.createdAt);
      switch(earningsFilter) {
        case 'today': return orderDate >= today;
        case 'week': return orderDate >= weekAgo;
        case 'month': return orderDate >= monthStart;
        default: return true;
      }
    });
  };
  
  const getOrderEarning = (order) => {
    return parseFloat(order.compensoRider) || parseFloat(order.earning) || (3 + (order.calculatedDistance || 0) * 0.20);
  };

  const getOrderTotal = (order) => {
    return getOrderEarning(order) + (order.mancia || 0);
  };

  const filteredOrders = getFilteredOrders();
  const filteredEarnings = filteredOrders.reduce((sum, order) => sum + getOrderTotal(order), 0);
  const totalMance = filteredOrders.reduce((sum, order) => sum + (order.mancia || 0), 0);
  
  return (
    <div className="space-y-4 pb-24">
      <div className="bg-white p-2 rounded-xl shadow-md">
        <div className="flex gap-2">
          {[
            { id: 'today', label: 'Oggi' },
            { id: 'week', label: 'Settimana' },
            { id: 'month', label: 'Mese' },
            { id: 'all', label: 'Totale' }
          ].map(filter => (
            <button
              key={filter.id}
              onClick={() => setEarningsFilter(filter.id)}
              className={`flex-1 py-2 px-3 rounded-lg font-semibold transition ${
                earningsFilter === filter.id
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-br from-green-500 to-green-700 text-white p-6 rounded-xl shadow-lg">
        <h2 className="text-lg mb-2">
          {earningsFilter === 'today' && 'Guadagno Oggi'}
          {earningsFilter === 'week' && 'Guadagno Settimana'}
          {earningsFilter === 'month' && 'Guadagno Mese'}
          {earningsFilter === 'all' && 'Guadagno Totale'}
        </h2>
        <p className="text-4xl font-bold">€{filteredEarnings.toFixed(2)}</p>
        <div className="flex items-center gap-2 mt-2 text-green-100">
          <TrendingUp size={16} />
          <span className="text-sm">{filteredOrders.length} ordini completati</span>
        </div>
        {totalMance > 0 && (
          <div className="mt-2 bg-green-600 bg-opacity-50 rounded-lg px-3 py-1 text-sm">
            🎁 Di cui €{totalMance.toFixed(2)} in mance
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <Package className="text-blue-600" size={20} />
            <span className="text-sm text-gray-600">Ordini completati</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{filteredOrders.length}</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="text-green-600" size={20} />
            <span className="text-sm text-gray-600">Media per ordine</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">
            €{filteredOrders.length > 0 ? (filteredEarnings / filteredOrders.length).toFixed(2) : '0.00'}
          </p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow-md">
        <h3 className="font-bold mb-3">Storico Ordini</h3>
        {filteredOrders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Package className="mx-auto mb-2" size={48} />
            <p>Nessun ordine in questo periodo</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredOrders.slice().reverse().map((order, idx) => (
              <div key={idx} className="border border-gray-200 p-3 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-800">{order.pharmacyName}</p>
                    <p className="text-xs text-gray-400 font-mono">#{order.id?.substring(0, 8)}</p>
                    <p className="text-sm text-gray-500">{order.customerName}</p>
                  </div>
                  <span className="text-green-600 font-bold">€{getOrderTotal(order).toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                  <span>{(order.calculatedDistance || 0).toFixed(1)} km</span>
                  <span>{Array.isArray(order.items) ? order.items.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0} prodotti</span>
                  <span>{new Date(order.deliveredAt || order.createdAt).toLocaleDateString('it-IT')}</span>
                  <span>{new Date(order.deliveredAt || order.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                  {order.mancia > 0 && (
                    <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-bold">
                      🎁 Mancia +€{order.mancia.toFixed(2)}
                    </span>
                  )}
                </div>
                {/* Dettaglio incasso contanti: quanto ha incassato e quanto è anticipo
                    farmacia (che verrà detratto in fattura al rider). */}
                {order.paymentMethod === 'cash' && (
                  <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-2">
                    <div className="bg-green-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-green-600 font-bold">💵 Incassato in contanti</p>
                      <p className="text-sm font-black text-green-700">€{(order.total || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-orange-600 font-bold">↩️ Quota consegna (da scalare)</p>
                      <p className="text-sm font-black text-orange-700">
                        €{(order.contanti_daScalareInLiquidazione != null
                            ? order.contanti_daScalareInLiquidazione
                            : (order.costoConsegnaIncassato || 0)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

  // CHAT VIEW
  const ChatView = () => {
  const [realtimeMessages, setRealtimeMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = React.useRef(null);

  const quickReplies = [
    'Arrivo tra 5 minuti 🛵',
    'Sono sotto casa tua 📍',
    'Sto suonando il citofono 🔔',
    'Ordine consegnato ✅',
    'Mi serve il numero del citofono 🏠'
  ];

  useEffect(() => {
    if (!activeOrder?.id) return;
    const q = query(
      collection(db, 'orders', activeOrder.id, 'chat'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      
      // Notifica per nuovi messaggi dall'admin
      if (msgs.length > realtimeMessages.length) {
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg.sender === 'admin') {
          if (Notification.permission === 'granted') {
            new Notification('Risposta dal Supporto 🟠', {
              body: lastMsg.text,
              icon: '/logo192.png'
            });
          }
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
      }
      
      setRealtimeMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return () => unsubscribe();
  }, [activeOrder?.id]);

  const sendRiderMessage = async (text) => {
    const msg = text || chatInput.trim();
    if (!msg || !activeOrder?.id) return;
    setChatInput('');
    try {
      await addDoc(collection(db, 'orders', activeOrder.id, 'chat'), {
        text: msg,
        sender: 'rider',
        senderName: currentRider?.name || 'Rider',
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Errore invio messaggio:', error);
    }
  };

  if (!activeOrder) {
    return (
      <div className="flex flex-col pb-24" style={{ height: '100vh' }}>
        <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-3">
          <button onClick={() => setCurrentView('map')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ChevronRight className="rotate-180" size={24} />
          </button>
          <h2 className="font-bold text-lg">Chat con Cliente</h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-8 text-center text-gray-500">
          <div>
            <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-semibold">Nessuna consegna attiva</p>
            <p className="text-sm mt-2">La chat sarà disponibile durante una consegna</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col pb-24" style={{ height: '100vh' }}>
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentView('map')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ChevronRight className="rotate-180" size={24} />
            </button>
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <User className="text-green-600" size={20} />
            </div>
            <div>
              <h2 className="font-bold">{activeOrder.customerName || 'Cliente'}</h2>
              <p className="text-xs text-green-600">🟢 In consegna</p>
            </div>
          </div>
          {activeOrder.customerPhone && (
            
            <a href={`tel:${activeOrder.customerPhone}`}
              className="flex items-center gap-2 bg-green-600 text-white px-3 py-2 rounded-xl font-semibold text-sm"
            >
              <Phone size={16} />
              Chiama
            </a>
          )}
        </div>
      </div>

      {/* RISPOSTE RAPIDE */}
      <div className="bg-gray-50 p-3 border-b border-gray-200">
        <p className="text-xs text-gray-500 mb-2">Risposte rapide:</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {quickReplies.map((msg, idx) => (
            <button
              key={idx}
              onClick={() => sendRiderMessage(msg)}
              className="flex-shrink-0 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:border-green-500 hover:text-green-600 transition"
            >
              {msg}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {realtimeMessages.length === 0 && (
          <div className="text-center text-gray-400 py-8">
            <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nessun messaggio. Inizia la conversazione!</p>
          </div>
        )}
        {realtimeMessages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === 'rider' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${
              msg.sender === 'rider'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-800 border border-gray-200'
            } p-3 rounded-2xl shadow-sm`}>
              <p className="text-sm">{msg.text}</p>
              <p className={`text-xs mt-1 ${msg.sender === 'rider' ? 'text-green-100' : 'text-gray-400'}`}>
                {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '...'}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendRiderMessage()}
            placeholder="Scrivi al cliente..."
            className="flex-1 px-4 py-3 rounded-xl bg-gray-100 border-0 focus:ring-2 focus:ring-green-500 outline-none text-sm"
          />
          <button
            onClick={() => sendRiderMessage()}
            className="bg-green-600 text-white px-5 py-3 rounded-xl font-semibold hover:bg-green-700 transition"
          >
            Invia
          </button>
        </div>
      </div>
    </div>
  );
};



// PROFILE VIEW
const ProfileView = () => {
  const [photoLoading, setPhotoLoading] = React.useState(false);
  const [showPasswordForm, setShowPasswordForm] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState('');
  const [passwordSuccess, setPasswordSuccess] = React.useState(false);
  const [mezzoEdit, setMezzoEdit] = React.useState(false);
  const [mezzoValue, setMezzoValue] = React.useState(currentRider?.mezzoTrasporto || '');

  const handlePhotoChange = async (file) => {
  if (!file) return;
  if (file.size > 5 * 1024 * 1024) {
    alert('⚠️ La foto è troppo grande. Massimo 5MB.');
    return;
  }
  setPhotoLoading(true);
  try {
    const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
    const storage = getStorage();
    const ext = (file.type === 'image/heic' || file.type === 'image/heif') ? 'heic' : 'jpg';
    const storageRef = ref(storage, `foto-profilo-rider/${currentRider.id}_${Date.now()}.${ext}`);
    await uploadBytes(storageRef, file);
    const downloadUrl = await getDownloadURL(storageRef);
    await updateDoc(doc(db, 'riders', currentRider.id), {
      photoPending: downloadUrl,
      photoStatus: 'pending',
      photoRequestedAt: new Date().toISOString()
    });
    setCurrentRider({ ...currentRider, photoPending: downloadUrl, photoStatus: 'pending' });
    alert('✅ Foto caricata! Sarà visibile dopo l\'approvazione dell\'amministratore.');
  } catch (error) {
    alert('❌ Errore: ' + error.message);
  } finally {
    setPhotoLoading(false);
  }
};

const handleFotoClick = () => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/jpeg,image/png,image/heic,image/heif,image/*';
  input.style.position = 'fixed';
  input.style.top = '-100px';
  input.style.left = '-100px';
  input.style.opacity = '0';
  document.body.appendChild(input);
  input.addEventListener('change', (e) => {
    const file = e.target.files[0];
    document.body.removeChild(input);
    if (file) handlePhotoChange(file);
  });
  input.addEventListener('cancel', () => {
    document.body.removeChild(input);
  });
  input.click();
};

  const handlePasswordChange = async () => {
    if (newPassword.length < 6) {
      setPasswordError('La password deve essere di almeno 6 caratteri');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Le password non coincidono');
      return;
    }
    try {
      const { updatePassword } = await import('firebase/auth');
      await updatePassword(auth.currentUser, newPassword);
      setPasswordSuccess(true);
      setPasswordError('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => { setPasswordSuccess(false); setShowPasswordForm(false); }, 2000);
    } catch (error) {
      if (error.code === 'auth/requires-recent-login') {
        setPasswordError('Sessione scaduta. Esegui il logout e accedi di nuovo.');
      } else {
        setPasswordError('Errore durante il cambio password');
      }
    }
  };

  const handleMezzoSave = async () => {
    try {
      await updateDoc(doc(db, 'riders', currentRider.id), {
        mezzoTrasporto: mezzoValue
      });
      setCurrentRider({ ...currentRider, mezzoTrasporto: mezzoValue });
      setMezzoEdit(false);
    } catch (error) {
      alert('Errore durante il salvataggio');
    }
  };

  return (
    <div className="space-y-4 pb-24">
      {/* FOTO E INFO PRINCIPALI */}
      <div className="bg-white p-6 rounded-xl shadow-md">
        <div className="flex flex-col items-center">
          <div className="relative mb-4">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-green-100 flex items-center justify-center">
              {currentRider?.photoUrl ? (
                <img src={currentRider.photoUrl} alt="Profilo" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-black text-green-600">
                  {currentRider?.name?.split(' ').map(n => n[0]).join('') || '?'}
                </span>
              )}
            </div>
            {currentRider?.photoStatus === 'pending' && (
              <div className="mt-2 px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-bold text-center">
                ⏳ Foto in attesa di approvazione
              </div>
            )}
            {currentRider?.photoStatus === 'rejected' && (
              <div className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold text-center">
                ❌ Foto rifiutata — carica una nuova foto
              </div>
            )}
            <button
  type="button"
  onClick={handleFotoClick}
  className="absolute bottom-0 right-0 bg-green-600 rounded-full p-2 cursor-pointer shadow-lg border-0"
>
  {photoLoading ? (
    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
  ) : (
    <Camera className="text-white" size={14} />
  )}
</button>
          </div>
          <h2 className="text-xl font-black text-gray-900">{currentRider?.name}</h2>
          <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mt-1">ID: {currentRider?.id}</p>
          <p className="text-sm text-gray-500">{currentRider?.email}</p>
          <span className="mt-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-bold">✅ Rider Attivo</span>
        </div>
      </div>

      {/* WALLET STRIPE */}
      <WalletSection />

      {/* MEZZO DI TRASPORTO */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Bike className="text-green-600" size={20} />
            <h3 className="font-bold text-gray-900">Mezzo di Trasporto</h3>
          </div>
          {!mezzoEdit && (
            <button onClick={() => setMezzoEdit(true)} className="text-sm text-green-600 font-semibold">
              Modifica
            </button>
          )}
        </div>
        {mezzoEdit ? (
          <div className="space-y-3">
            <select
              value={mezzoValue}
              onChange={(e) => setMezzoValue(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none"
            >
              <option value="bicicletta">🚲 Bicicletta</option>
              <option value="e-bike">⚡ E-Bike</option>
              <option value="scooter">🛵 Scooter</option>
              <option value="auto">🚗 Auto</option>
            </select>
            <div className="flex gap-2">
              <button onClick={handleMezzoSave} className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold">Salva</button>
              <button onClick={() => setMezzoEdit(false)} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-bold">Annulla</button>
            </div>
          </div>
        ) : (
          <p className="text-gray-700 font-semibold">
            {currentRider?.mezzoTrasporto === 'bicicletta' && '🚲 Bicicletta'}
            {currentRider?.mezzoTrasporto === 'e-bike' && '⚡ E-Bike'}
            {currentRider?.mezzoTrasporto === 'scooter' && '🛵 Scooter'}
            {currentRider?.mezzoTrasporto === 'auto' && '🚗 Auto'}
            {!currentRider?.mezzoTrasporto && '— Non specificato'}
          </p>
        )}
      </div>

      {/* CAMBIO PASSWORD */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Key className="text-green-600" size={20} />
            <h3 className="font-bold text-gray-900">Password</h3>
          </div>
          {!showPasswordForm && (
            <button onClick={() => setShowPasswordForm(true)} className="text-sm text-green-600 font-semibold">
              Cambia
            </button>
          )}
        </div>
        {showPasswordForm ? (
          <div className="space-y-3">
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Nuova password"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none text-sm"
            />
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Conferma password"
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none text-sm"
            />
            {passwordError && (
              <p className="text-red-600 text-sm font-semibold">{passwordError}</p>
            )}
            {passwordSuccess && (
              <p className="text-green-600 text-sm font-semibold">✅ Password aggiornata!</p>
            )}
            <div className="flex gap-2">
              <button onClick={handlePasswordChange} className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold">Salva</button>
              <button onClick={() => { setShowPasswordForm(false); setPasswordError(''); }} className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg font-bold">Annulla</button>
            </div>
          </div>
        ) : (
          <p className="text-gray-500 text-sm">••••••••</p>
        )}
      </div>

      {/* RECENSIONI */}
      <RecensioniSection />

      {/* LOGOUT */}
      <button
        onClick={handleLogout}
        className="w-full bg-red-50 border border-red-200 text-red-600 py-4 rounded-xl font-bold hover:bg-red-100 transition flex items-center justify-center gap-2"
      >
        <LogOut size={20} />
        Logout
      </button>
    </div>
  );
};

// SEZIONE PAGAMENTI — IBAN (bonifico manuale mensile)
// Campo importo pagato in farmacia (ordini ricetta) — componente ISOLATO e
// memoizzato: mantiene uno stato locale così i re-render del genitore (dovuti
// all'aggiornamento GPS ogni 7s) non lo rimontano e non fanno perdere il focus
// alla tastiera mentre il rider scrive.
const WalletSection = () => {
  const [iban, setIban] = React.useState(currentRider?.iban || '');
  const [salvando, setSalvando] = React.useState(false);
  const [editing, setEditing] = React.useState(!currentRider?.iban);

  // Normalizza e valida un IBAN italiano (IT + 25 caratteri = 27 totali)
  const pulisciIban = (v) => v.replace(/\s+/g, '').toUpperCase();
  const ibanValido = (v) => /^IT\d{2}[A-Z0-9]{23}$/.test(pulisciIban(v));

  const salvaIban = async () => {
    const ibanPulito = pulisciIban(iban);
    if (!ibanValido(ibanPulito)) {
      alert('⚠️ IBAN non valido. Deve iniziare con IT ed essere lungo 27 caratteri (es. IT60X0542811101000000123456).');
      return;
    }
    setSalvando(true);
    try {
      await updateDoc(doc(db, 'riders', currentRider.id), {
        iban: ibanPulito,
        ibanAggiornatoAt: new Date().toISOString(),
      });
      setCurrentRider(prev => ({ ...prev, iban: ibanPulito }));
      setEditing(false);
      alert('✅ IBAN salvato correttamente.');
    } catch (e) {
      alert('❌ Errore: ' + e.message);
    } finally {
      setSalvando(false);
    }
  };

  // Formatta l'IBAN a gruppi di 4 per la visualizzazione
  const ibanFormattato = (v) => pulisciIban(v).replace(/(.{4})/g, '$1 ').trim();

  return (
    <div className="bg-white p-4 rounded-xl shadow-md">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xl">🏦</span>
        <h3 className="font-bold text-gray-900">Pagamenti — IBAN</h3>
      </div>

      {!editing && currentRider?.iban ? (
        <div className="space-y-3">
          <div className="bg-green-50 border border-green-200 p-3 rounded-xl">
            <p className="text-xs font-bold text-green-700 mb-1">✅ IBAN registrato</p>
            <p className="font-mono text-sm text-green-900 tracking-wide">{ibanFormattato(currentRider.iban)}</p>
          </div>
          <p className="text-xs text-gray-500">
            Riceverai il compenso tramite bonifico bancario entro il giorno 12 del mese successivo, dopo aver ricevuto il prospetto via email.
          </p>
          <button onClick={() => setEditing(true)}
            className="w-full bg-gray-100 text-gray-700 py-2 rounded-xl font-bold text-sm hover:bg-gray-200 transition">
            ✏️ Modifica IBAN
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-xl">
            <p className="text-sm font-bold text-blue-800">Inserisci il tuo IBAN</p>
            <p className="text-xs text-blue-600 mt-1">Serve per ricevere il compenso mensile e il rimborso di quanto anticipi in farmacia. Trovi l'IBAN nell'app della tua banca o sull'estratto conto.</p>
          </div>
          <input
            type="text"
            value={iban}
            onChange={e => setIban(e.target.value)}
            placeholder="IT60 X054 2811 1010 0000 0123 456"
            className="w-full px-3 py-3 rounded-xl border-2 border-gray-300 font-mono text-sm outline-none focus:border-blue-500 uppercase"
          />
          <button onClick={salvaIban} disabled={salvando}
            className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2">
            {salvando ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Salvataggio...</> : '💾 Salva IBAN'}
          </button>
          {currentRider?.iban && (
            <button onClick={() => { setIban(currentRider.iban); setEditing(false); }}
              className="w-full text-gray-500 py-1 text-sm font-semibold">
              Annulla
            </button>
          )}
        </div>
      )}

      <div className="mt-4 bg-gray-50 p-3 rounded-xl">
        <p className="text-xs font-bold text-gray-700 mb-2">📊 Compenso per consegna:</p>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="bg-white p-2 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500">Base</p>
            <p className="font-black text-green-600">€3,00</p>
          </div>
          <div className="bg-white p-2 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500">Per km</p>
            <p className="font-black text-blue-600">€0,20</p>
          </div>
          <div className="bg-white p-2 rounded-lg border border-gray-200">
            <p className="text-xs text-gray-500">Festivo</p>
            <p className="font-black text-orange-600">+20%</p>
          </div>
        </div>
      </div>
    </div>
  );
};


// RECENSIONI SECTION
const RecensioniSection = () => {
  const [recensioni, setRecensioni] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!currentRider?.id) return;
    const q = query(
      collection(db, 'riders', currentRider.id, 'recensioni'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      setRecensioni(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return () => unsubscribe();
  }, [currentRider?.id]);

  const media = currentRider?.ratingMedia || 0;
  const count = currentRider?.ratingCount || 0;

  const renderStelle = (n) => '⭐'.repeat(n) + '☆'.repeat(5 - n);

  return (
    <div className="bg-white p-4 rounded-xl shadow-md">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xl">⭐</span>
        <h3 className="font-bold text-gray-900">Le mie Recensioni</h3>
      </div>

      {/* Media stelle */}
      <div className="bg-yellow-50 rounded-xl p-4 text-center mb-4">
        <p className="text-4xl font-black text-yellow-600">{media > 0 ? media.toFixed(1) : '—'}</p>
        <p className="text-yellow-500 text-lg mt-1">{media > 0 ? renderStelle(Math.round(media)) : '☆☆☆☆☆'}</p>
        <p className="text-xs text-gray-500 mt-1">{count} recensioni totali</p>
      </div>

      {loading ? (
        <div className="text-center py-4 text-gray-400">Caricamento...</div>
      ) : recensioni.length === 0 ? (
        <div className="text-center py-6 text-gray-400">
          <p className="text-4xl mb-2">💬</p>
          <p className="font-semibold">Nessuna recensione ancora</p>
          <p className="text-sm mt-1">Le recensioni appariranno dopo le consegne</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {recensioni.map(r => (
            <div key={r.id} className="border border-gray-100 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm">{renderStelle(r.stelle)}</span>
                <span className="text-xs text-gray-400">
                  {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleDateString('it-IT') : ''}
                </span>
              </div>
              {r.commento && (
                <p className="text-sm text-gray-700 italic">"{r.commento}"</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// DAILY DISCLAIMER
const DailyDisclaimer = () => {
  const [checked, setChecked] = React.useState(false);

  const handleAccept = () => {
    const today = new Date().toISOString().split('T')[0];
    localStorage.setItem('disclaimer-accepted', today);
    setShowDailyDisclaimer(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
        <div className="bg-green-600 p-5 text-white text-center">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
            <span className="text-3xl">📋</span>
          </div>
          <h2 className="text-xl font-black">Autodichiarazione</h2>
          <p className="text-green-100 text-sm mt-1">Da completare ogni giorno prima di iniziare</p>
        </div>

        <div className="p-5 space-y-4 max-h-96 overflow-y-auto">
          <p className="text-sm text-gray-700 font-semibold">Accedendo a questa app dichiari che:</p>
          
          <div className="space-y-3 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold mt-0.5">•</span>
              <p>Il mezzo che utilizzerai per le consegne è in <strong>buono stato di manutenzione</strong> ed è idoneo alla circolazione su strada</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold mt-0.5">•</span>
              <p>Sei in possesso di tutti i <strong>documenti validi</strong> richiesti per la circolazione</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold mt-0.5">•</span>
              <p>Sei <strong>fisicamente idoneo</strong> a svolgere attività di consegna oggi</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold mt-0.5">•</span>
              <p>Hai preso visione e accetti le <strong>condizioni di servizio</strong> di FarmaciaSubito</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-green-600 font-bold mt-0.5">•</span>
              <p>Sei consapevole che dichiarazioni mendaci possono comportare <strong>conseguenze legali e contrattuali</strong></p>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-gray-100">
          <button
            onClick={() => setChecked(!checked)}
            className="flex items-center gap-3 w-full mb-4"
          >
            <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 transition ${
              checked ? 'bg-green-600 border-green-600' : 'border-gray-400'
            }`}>
              {checked && <Check className="text-white" size={14} />}
            </div>
            <span className="text-sm font-semibold text-gray-800 text-left">
              Confermo e accetto tutte le dichiarazioni sopra riportate
            </span>
          </button>

          <button
            onClick={handleAccept}
            disabled={!checked}
            className="w-full bg-green-600 text-white py-4 rounded-xl font-black text-lg hover:bg-green-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Accetta e continua
          </button>
        </div>
      </div>
    </div>
  );
};

// MODAL PRODOTTO NON TROVATO
  const ProdottoNonTrovatoModal = () => {
    if (!showProdottoNonTrovato || !activeOrder) return null;

    const items = activeOrder.items || [];
    const isRicetta = activeOrder.isRicettaOrder === true || items.length === 0;
    const [selezionati, setSelezionati] = React.useState([]);
    const [loading, setLoading] = React.useState(false);

    const toggleItem = (itemId) => {
      setSelezionati(prev =>
        prev.includes(itemId) ? prev.filter(i => i !== itemId) : [...prev, itemId]
      );
    };

    const conferma = async () => {
      // Per gli ordini normali serve almeno un prodotto selezionato.
      // Per gli ordini ricetta non c'è lista: si segnala il farmaco come non disponibile.
      if (!isRicetta && selezionati.length === 0) {
        alert('Seleziona almeno un prodotto non disponibile');
        return;
      }
      setLoading(true);
      try {
        const tuttiNonDisponibili = isRicetta || selezionati.length === items.length;

        const itemsSegnalati = isRicetta
          ? [{ name: 'Farmaco su ricetta', quantity: 1, price: 0 }]
          : items
              .filter(i => selezionati.includes(i.id || i.name))
              .map(i => ({ name: i.name, quantity: i.quantity, price: i.price }));

        await updateDoc(doc(db, 'orders', activeOrder.id), {
          status: 'in_revisione_admin',
          revisioneMotivo: isRicetta ? 'Farmaco su ricetta non disponibile in farmacia' : 'Prodotti non disponibili in farmacia',
          revisioneTipo: tuttiNonDisponibili ? 'totale' : 'parziale',
          prodottiNonDisponibili: isRicetta ? ['ricetta'] : selezionati,
          prodottiNonDisponibiliDettaglio: itemsSegnalati,
          segnalatoBy: 'rider',
          segnalatoRiderId: currentRider?.id || null,
          segnalatoRiderName: currentRider?.name || null,
          segnalatoAt: new Date().toISOString(),
        });

        alert('✅ Segnalazione inviata. L\'ordine è ora in revisione: l\'amministrazione deciderà come procedere e gestirà l\'eventuale rimborso. Sei libero per nuovi ordini.');

        setShowProdottoNonTrovato(false);
        setActiveOrder(null);
        setProdottiRitirati(false);
        setScontrinoUrl(null);
        setImportoPagato('');
        clearRiderPosition();
        if (currentRider?.id) {
          try {
            await updateDoc(doc(db, 'riders', currentRider.id), { activeOrderId: null });
          } catch (e) {
            console.warn('Impossibile pulire activeOrderId rider:', e);
          }
        }
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
          <div className="bg-orange-600 p-4 text-white">
            <h3 className="text-lg font-black">📋 Segnala prodotto non disponibile</h3>
            <p className="text-orange-100 text-sm">
              {isRicetta
                ? "Segnala che la farmacia non ha il farmaco della ricetta. L'ordine andrà in revisione admin."
                : "Seleziona i prodotti che la farmacia non ha. L'ordine andrà in revisione admin."}
            </p>
          </div>
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {isRicetta ? (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 text-center">
                <div className="text-4xl mb-2">💊</div>
                <p className="font-black text-gray-900">Farmaco su ricetta</p>
                <p className="text-xs text-gray-500 mt-1">Premi "Invia segnalazione" per segnalare che la farmacia non ha il farmaco richiesto.</p>
              </div>
            ) : (
              items.map((item, idx) => {
                const itemKey = item.id || item.name;
                const isSelected = selezionati.includes(itemKey);
                return (
                  <button
                    key={idx}
                    onClick={() => toggleItem(itemKey)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition text-left ${
                      isSelected ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'bg-red-500 border-red-500' : 'border-gray-300'
                    }`}>
                      {isSelected && <X className="text-white" size={14} />}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 text-sm">{item.name}</p>
                      <p className="text-xs text-gray-500">Quantità: {item.quantity} — €{(item.price * item.quantity).toFixed(2)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
          {selezionati.length > 0 && (
            <div className="mx-4 p-3 rounded-xl text-sm font-semibold bg-orange-50 text-orange-700">
              {selezionati.length === items.length
                ? '📋 Tutti i prodotti segnalati come non disponibili. L\'ordine andrà in revisione: deciderà l\'amministrazione.'
                : `📋 ${selezionati.length} prodotto/i segnalato/i. L\'ordine andrà in revisione amministrativa.`
              }
            </div>
          )}
          <div className="p-4 flex gap-3">
            <button
              onClick={() => setShowProdottoNonTrovato(false)}
              className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold"
            >
              Annulla
            </button>
            <button
              onClick={conferma}
              disabled={loading || (!isRicetta && selezionati.length === 0)}
              className="flex-1 bg-orange-600 text-white py-3 rounded-xl font-bold hover:bg-orange-700 transition disabled:bg-gray-400"
            >
              {loading ? 'Invio...' : 'Invia segnalazione'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // POPUP CODICE FISCALE
  const CodiceFiscalePopup = () => {
    if (!showCodiceFiscalePopup) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
          <div className="bg-orange-600 p-5 text-white text-center">
            <div className="text-4xl mb-2">{codiceFiscaleStep === 1 ? '🪪' : '🧾'}</div>
            <h2 className="text-xl font-black">
              {codiceFiscaleStep === 1 ? 'Codice Fiscale Cliente' : 'Scontrino / Fattura'}
            </h2>
          </div>

          <div className="p-5">
            {codiceFiscaleStep === 1 ? (
              <>
                <p className="text-gray-700 text-sm mb-4 text-center">
                  Il cliente ha richiesto l'emissione dello <strong>scontrino parlante</strong>.<br />
                  Comunica questo codice fiscale alla farmacia:
                </p>
                <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-4 text-center mb-6">
                  <p className="text-xs text-orange-600 font-bold mb-1">CODICE FISCALE</p>
                  <p className="text-2xl font-black text-orange-800 tracking-widest">
                    {activeOrder?.codiceFiscale}
                  </p>
                </div>
                <button
                  onClick={() => setCodiceFiscaleStep(2)}
                  className="w-full bg-orange-600 text-white py-4 rounded-xl font-black text-lg hover:bg-orange-700 transition"
                >
                  ✅ Ho comunicato il codice fiscale
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-700 text-sm mb-6 text-center">
                  Hai ritirato lo <strong>scontrino o la fattura</strong> dalla farmacia da consegnare al cliente?
                </p>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      setShowCodiceFiscalePopup(false);
                      setProdottiRitirati(true);
                    }}
                    className="w-full bg-green-600 text-white py-4 rounded-xl font-black text-lg hover:bg-green-700 transition"
                  >
                    ✅ Sì, ho ritirato lo scontrino
                  </button>
                  <button
                    onClick={() => setCodiceFiscaleStep(1)}
                    className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition"
                  >
                    ← Torna indietro
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // INCOMING ORDER POPUP
  const OrderPopup = () => {
    if (!incomingOrder) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Nuovo Ordine!</h2>
              <div className="bg-white text-green-600 px-4 py-2 rounded-full font-bold text-xl">
                {orderTimer}s
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Bell className="animate-pulse" size={24} />
              <span className="text-green-100">Accetta prima che scada</span>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <p className="text-sm text-gray-500 mb-1">Farmacia</p>
              <p className="font-bold text-lg">{incomingOrder.pharmacy}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500 mb-1">Destinazione</p>
              <div className="flex items-center gap-2">
                <MapPin className="text-gray-600" size={16} />
                <p className="font-semibold">{incomingOrder.address}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
              <div className="text-center">
                <Navigation className="text-gray-600 mx-auto mb-1" size={20} />
                <p className="text-sm text-gray-500">Distanza</p>
                <p className="font-bold">{incomingOrder.distance} km</p>
              </div>
              <div className="text-center">
                <Package className="text-gray-600 mx-auto mb-1" size={20} />
                <p className="text-sm text-gray-500">Prodotti</p>
                <p className="font-bold">{incomingOrder.itemsCount}</p>
              </div>
              <div className="text-center">
                <DollarSign className="text-green-600 mx-auto mb-1" size={20} />
                <p className="text-sm text-gray-500">Guadagno</p>
                <p className="font-bold text-green-600">€{incomingOrder.earning}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-4">
              <button
                onClick={async () => {
                  try {
                    // Rifiuto: aggiungi rider a riderGiaNotificati (array semplice, NO timestamp)
                    await updateDoc(doc(db, 'orders', incomingOrder.id), {
                      assignedRiderId: null,
                      assignedRiderName: null,
                      assignedAt: null,
                      riderGiaNotificati: arrayUnion(currentRider.id)
                    });
                    // Pulisci activeOrderId sul rider — torna libero per altri ordini
                    await updateDoc(doc(db, 'riders', currentRider.id), { activeOrderId: null });
                    // LOGICA PACATA: NON chiamiamo assegnaRider — l'ordine resta fermo
                    // fino al prossimo giro dello scheduler (max 2 min). Il telefono respira.
                  } catch(e) { console.error(e); }
                  setIncomingOrder(null);
                  setOrderTimer(25);
                }}
                className="bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Rifiuta
              </button>
              <button
                onClick={acceptOrder}
                className="bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition"
              >
                Accetta
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // BOTTOM NAVIGATION
  const BottomNav = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-40">
      <div className="flex justify-around items-center max-w-md mx-auto">
        {[
          { id: 'map', icon: MapPin, label: 'Mappa' },
          { id: 'calendar', icon: Calendar, label: 'Turni' },
          { id: 'earnings', icon: DollarSign, label: 'Guadagni' },
          { id: 'support', icon: AlertCircle, label: 'Supporto' },
          { id: 'profile', icon: User, label: 'Profilo' }
        ].map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setCurrentView(item.id)}
              className={`flex flex-col items-center gap-1 ${
                currentView === item.id ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              <Icon size={24} />
              <span className="text-xs font-semibold">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // NOTIFICATION
  const Notification = () => {
    if (!showNotification) return null;
    
    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg z-50 flex items-center gap-2">
        <Bell size={20} />
        <span className="font-semibold">Nuovo ordine disponibile!</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* POPUP SCELTA FOTOCAMERA / GALLERIA per lo scontrino */}
      {showScontrinoChoice && (
        <div
          onClick={() => setShowScontrinoChoice(false)}
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4"
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3">
            <h3 className="font-black text-gray-900 text-lg text-center">📸 Carica scontrino</h3>
            <p className="text-sm text-gray-500 text-center mb-2">Scegli come aggiungere la foto</p>
            <button
              onClick={() => { setShowScontrinoChoice(false); apriSelettoreScontrino(true); }}
              className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition flex items-center justify-center gap-2"
            >
              📷 Scatta foto ora
            </button>
            <button
              onClick={() => { setShowScontrinoChoice(false); apriSelettoreScontrino(false); }}
              className="w-full bg-blue-50 border border-blue-200 text-blue-700 py-3 rounded-xl font-bold hover:bg-blue-100 transition flex items-center justify-center gap-2"
            >
              🖼️ Scegli dalla galleria
            </button>
            <button
              onClick={() => setShowScontrinoChoice(false)}
              className="w-full text-gray-500 py-2 text-sm font-semibold"
            >
              Annulla
            </button>
          </div>
        </div>
      )}

      {ricettaFullscreen && (
        <div
          onClick={() => setRicettaFullscreen(null)}
          className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center p-2"
        >
          <div className="w-full flex justify-between items-center px-2 py-3">
            <p className="text-white font-bold text-sm">📋 Ricetta — mostra al farmacista</p>
            <button
              onClick={() => setRicettaFullscreen(null)}
              className="bg-white/20 text-white px-4 py-2 rounded-lg font-bold text-sm"
            >
              ✕ Chiudi
            </button>
          </div>
          <div className="flex-1 w-full flex items-center justify-center overflow-auto" onClick={(e) => e.stopPropagation()}>
            <img
              src={ricettaFullscreen}
              alt="Ricetta a schermo intero"
              className="max-w-full max-h-full object-contain"
              style={{ backgroundColor: '#fff' }}
            />
          </div>
          <p className="text-white/60 text-xs py-2">Tocca fuori dall'immagine per chiudere</p>
        </div>
      )}
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-30">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
              <div className="text-white text-xl font-bold">+</div>
            </div>
            <div>
              <h1 className="font-bold text-lg">Ciao, {currentRider.name.split(' ')[0]}!</h1>
              <p className="text-xs text-gray-500">Rider</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <LogOut className="text-gray-600" size={20} />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        {currentView === 'map' && <MapView />}
        {currentView === 'calendar' && <CalendarView />}
        {currentView === 'earnings' && <EarningsView />}
        {currentView === 'chat' && <ChatView />}
        <div style={{ display: currentView === 'support' ? 'block' : 'none' }}>
  <SupportView currentRider={currentRider} db={db} setCurrentView={setCurrentView} />
</div>
      </div>
      {currentView === 'profile' && <ProfileView />}
      {currentView !== 'chat' && <BottomNav />}
      <CodiceFiscalePopup />
      <OrderPopup />
      <ProdottoNonTrovatoModal />
      <Notification />
      {showDailyDisclaimer && <DailyDisclaimer />}

      {/* MODAL RINUNCIA ORDINE */}
      {showRinunciaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-red-600 p-5 text-white">
              <h2 className="text-xl font-bold">Rinuncia all'ordine</h2>
              <p className="text-red-100 text-sm mt-1">L'ordine verrà riassegnato a un altro rider</p>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-gray-700 mb-2">Seleziona il motivo:</p>
              {[
                'Problemi con il veicolo',
                'Non voglio più completare la consegna',
                'Indirizzo non raggiungibile',
                'Altro'
              ].map((motivo) => (
                <button
                  key={motivo}
                  onClick={() => setRinunciaMotivoSelezionato(motivo)}
                  className={`w-full text-left px-4 py-3 rounded-xl border-2 font-semibold transition text-sm ${
                    rinunciaMotivoSelezionato === motivo
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {rinunciaMotivoSelezionato === motivo ? '🔴 ' : '⚪ '}
                  {motivo}
                </button>
              ))}
              {rinunciaMotivoSelezionato === 'Altro' && (
                <textarea
                  value={rinunciaMotivoAltro}
                  onChange={(e) => setRinunciaMotivoAltro(e.target.value)}
                  placeholder="Descrivi brevemente il motivo..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-400 outline-none text-sm resize-none"
                />
              )}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowRinunciaModal(false);
                    setRinunciaMotivoSelezionato('');
                    setRinunciaMotivoAltro('');
                  }}
                  className="py-3 rounded-xl bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
                >
                  Annulla
                </button>
                <button
                  onClick={rinunciaOrdine}
                  disabled={rinunciaLoading || !rinunciaMotivoSelezionato || (rinunciaMotivoSelezionato === 'Altro' && !rinunciaMotivoAltro.trim())}
                  className="py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition disabled:bg-gray-300 disabled:text-gray-400 flex items-center justify-center gap-2"
                >
                  {rinunciaLoading ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Invio...</>
                  ) : (
                    'Conferma Rinuncia'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmaciaSubitoRider;